'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ChangeEvent, FocusEvent, MouseEvent as ReactMouseEvent, ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useRouter } from 'next/navigation'
import { UserDropdown } from './UserDropdown'
import { StoreSelector } from './StoreSelector'
import { useUser } from '@/contexts/UserContext'
import { useUI } from '@/contexts/UIContext'
import { Modal } from '@/components/ui'
import { ToggleUI } from '@/components/ui/Toggle'
import { useToast } from '@/components/ui/ToastContainer'
import { useDeferredStartup } from '@/hooks/useDeferredStartup'

const API_BASE = process.env.NEXT_PUBLIC_API_URL || 'https://app.canerozen.com/api'
const STORE_SETTINGS_PINNED_KEY = 'panel_store_settings_pinned'
const NOTIFICATION_UNREAD_POLL_MS = 120000
const SETUP_TASKS_IDLE_POLL_MS = 300000
const SETUP_TASKS_OPEN_POLL_MS = 15000

function getInitials(name: string) {
  const n = String(name || '').trim()
  if (!n) return '??'
  const parts = n.split(/\s+/).filter(Boolean)
  const a = (parts[0] || '').slice(0, 1)
  const b = (parts[1] || '').slice(0, 1)
  return (a + b).toUpperCase() || a.toUpperCase() || '??'
}

function getToken() {
  if (typeof window === 'undefined') return ''
  return localStorage.getItem('auth_token') || ''
}

function isBrowserTabVisible() {
  return typeof document === 'undefined' || document.visibilityState === 'visible'
}

function getSetupApiHeaders(json = false) {
  const token = getToken()
  return {
    ...(json ? { 'Content-Type': 'application/json' } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

function formatNotificationTime(value?: string) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleString('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatSetupTaskDate(value?: number) {
  const timestamp = Number(value || 0)
  if (!timestamp) return ''
  const date = new Date(timestamp)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleString('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

interface NotificationItem {
  id: number
  type: string
  title?: string
  body?: string
  data?: Record<string, unknown> | null
  urgent?: boolean
  read_at?: string | null
  created_at?: string
}

interface TopBarProps {
  dashboardSelector?: ReactNode
}

interface StoreSettingsResponse {
  firmId: number
  storeId: number
  firmName?: string
  firmCode?: string
  storeName?: string
  storeCode?: string
  storeDomain?: string
  modules?: Array<{
    id: number
    name: string
    shortDesc?: string
    settingCount: number
    settings?: Record<string, string>
  }>
  cache: {
    flat?: Record<string, string>
    modules?: Record<string, Record<string, string>>
    list?: Array<Record<string, unknown>>
  }
}

type SetupTaskStatus = 'pending' | 'in_progress' | 'done' | 'not_needed' | 'cancelled'

interface SetupTaskItem {
  id: number
  system_product_id?: number | null
  setup_step?: string
  task_key?: string
  task_type?: string
  owner_type?: string
  title: string
  description?: string
  status: SetupTaskStatus
  priority?: string
  target_url?: string
  required_for_publish?: boolean
  payload?: Record<string, unknown>
  created_at?: number
  updated_at?: number
  completed_at?: number
}

interface SetupTaskSummary {
  total?: number
  pending?: number
  in_progress?: number
  done?: number
  not_needed?: number
  cancelled?: number
  required_open?: number
  training_open?: number
}

const SETUP_TASK_STATUS_LABELS: Record<SetupTaskStatus, string> = {
  pending: 'Bekliyor',
  in_progress: 'Devam ediyor',
  done: 'Tamamlandı',
  not_needed: 'Gerekli değil',
  cancelled: 'İptal',
}

const SETUP_TASK_TYPE_LABELS: Record<string, string> = {
  setup: 'Ayar',
  visual: 'Görsel',
  payment: 'Ödeme',
  integration: 'Entegrasyon',
  content: 'İçerik',
  training: 'Eğitim',
}

type StoreSettingsTab = 'json' | 'modules' | 'settings'

type SetupGuideStep = {
  id: string
  title: string
  eyebrow: string
  estimate: string
  summary: string
  details: string[]
  faqs: Array<{ question: string; answer: string }>
  videoTitle?: string
}

type SetupCompanyInfoForm = {
  legalName: string
  tradeName: string
  taxOffice: string
  taxNumber: string
  address: string
  city: string
  district: string
  companyEmail: string
  companyPhone: string
}

const SETUP_COMPANY_INFO_INITIAL: SetupCompanyInfoForm = {
  legalName: '',
  tradeName: '',
  taxOffice: '',
  taxNumber: '',
  address: '',
  city: '',
  district: '',
  companyEmail: '',
  companyPhone: '',
}

const SETUP_COMPANY_REQUIRED_FIELDS: Array<keyof SetupCompanyInfoForm> = [
  'legalName',
  'tradeName',
  'taxOffice',
  'taxNumber',
  'address',
  'city',
  'district',
  'companyEmail',
  'companyPhone',
]

type SetupAssetKey = 'logoDesktop' | 'logoMobile' | 'logoSvg' | 'favicon' | 'lazyload'

type SetupAssetPreview = {
  name: string
  src: string
}

type SetupPaymentLibraryItem = {
  id: number
  name: string
  pulled?: boolean
}

const SETUP_ASSET_REQUIREMENTS: Array<{
  key: SetupAssetKey
  title: string
  description: string
  formats: string
  size: string
  maxSize: string
  accept: string
}> = [
  {
    key: 'logoDesktop',
    title: 'Düz logo',
    description: 'Masaüstü header ve geniş ekran marka alanlarında kullanılacak logo.',
    formats: 'PNG, WEBP, JPG',
    size: 'Önerilen 320 x 120 px',
    maxSize: 'En fazla 2 MB',
    accept: '.png,.webp,.jpg,.jpeg,image/png,image/webp,image/jpeg',
  },
  {
    key: 'logoMobile',
    title: 'Mobil logo',
    description: 'Mobil header ve dar ekran marka alanlarında kullanılacak logo.',
    formats: 'PNG, WEBP, JPG',
    size: 'Önerilen 240 x 90 px',
    maxSize: 'En fazla 2 MB',
    accept: '.png,.webp,.jpg,.jpeg,image/png,image/webp,image/jpeg',
  },
  {
    key: 'logoSvg',
    title: 'SVG logo',
    description: 'Keskin ve ölçeklenebilir logo kullanımı için SVG kodu.',
    formats: 'SVG',
    size: 'SVG kodu',
    maxSize: 'En fazla 30 KB',
    accept: '',
  },
  {
    key: 'favicon',
    title: 'Favicon',
    description: 'Tarayıcı sekmesi ve kısa yol ikonlarında görünecek simge.',
    formats: 'ICO, PNG, SVG',
    size: 'Önerilen 64 x 64 px',
    maxSize: 'En fazla 512 KB',
    accept: '.ico,.png,.svg,image/x-icon,image/png,image/svg+xml',
  },
  {
    key: 'lazyload',
    title: 'LazyLoad',
    description: 'Görseller yüklenirken kullanılacak geçici marka görseli.',
    formats: 'SVG, PNG, WEBP',
    size: 'Önerilen 600 x 400 px',
    maxSize: 'En fazla 1 MB',
    accept: '.svg,.png,.webp,image/svg+xml,image/png,image/webp',
  },
]

const SETUP_LOGO_ASSETS = SETUP_ASSET_REQUIREMENTS.filter((asset) => asset.key.startsWith('logo'))
const SETUP_OTHER_ASSETS = SETUP_ASSET_REQUIREMENTS.filter((asset) => !asset.key.startsWith('logo'))

const SETUP_CARGO_PROVIDERS = [
  {
    id: 'hepsijet',
    title: 'Hepsijet',
    description: 'Pazaryeri ve hızlı teslimat senaryoları için kullanılabilir.',
    fields: ['integrator_name', 'merchant_id', 'api_key'],
  },
  {
    id: 'yurtici',
    title: 'Yurtiçi Kargo',
    description: 'Cari bazlı gönderi ve kapıda ödeme carileriyle çalışabilir.',
    fields: ['Cari_ID', 'Kredi kartı cari kodu', 'Kapıda ödeme cari kodu'],
  },
  {
    id: 'aras',
    title: 'Aras Kargo',
    description: 'Standart kargo entegrasyonu ve takip akışı için hazırlanır.',
    fields: ['Müşteri kodu', 'Kullanıcı adı', 'Şifre'],
  },
  {
    id: 'mng',
    title: 'MNG Kargo',
    description: 'Şube ve anlaşma bilgileriyle gönderi oluşturma sağlar.',
    fields: ['Müşteri numarası', 'Şube kodu', 'API şifresi'],
  },
]

const SETUP_CARGO_BANDS = [
  { label: '0 - 1 kg', price: '79,90' },
  { label: '1 - 3 kg', price: '99,90' },
  { label: '3 - 5 kg', price: '129,90' },
]

type SetupDetailPanel = {
  intro: string
  cards?: Array<{ title: string; description: string; icon: string }>
  groups: Array<{
    title: string
    description?: string
    fields?: Array<{ label: string; placeholder?: string; type?: string; wide?: boolean; optional?: boolean }>
    textarea?: { label: string; placeholder?: string; value?: string }
    chips?: string[]
  }>
}

const SETUP_DETAILED_STEPS: SetupGuideStep[] = [
  {
    id: 'faq-library',
    title: 'Sık sorulan soruları hazırlayın',
    eyebrow: 'SSS',
    estimate: '8 dk',
    summary: 'Kütüphaneden gelen sık sorulan sorular mağazanıza uygun şekilde düzenlenebilir.',
    details: ['Kütüphaneden önerilen sorular listelenir.', 'Firma isterse yanıt metinlerine müdahale eder.', 'Yayında görünecek soru sırası kontrol edilir.'],
    faqs: [{ question: 'Soruları değiştirebilir miyim?', answer: 'Evet. Kütüphane başlangıç için gelir, metinleri manuel düzenleyebilirsiniz.' }],
    videoTitle: 'SSS kütüphanesi',
  },
  {
    id: 'sms-call-settings',
    title: 'SMS ve robot arama bilgilerini girin',
    eyebrow: 'İletişim',
    estimate: '9 dk',
    summary: 'Telsam SMS, robot arama ve gönderilecek mesaj kuralları bu adımda hazırlanır.',
    details: ['SMS sağlayıcı bilgileri girilir.', 'Gidecek mesaj tipleri seçilir.', 'Robot arama senaryoları kontrol edilir.'],
    faqs: [{ question: 'SMS metinleri sabit mi?', answer: 'Hayır. Varsayılan metinler önerilir, firma ihtiyacına göre düzenlenebilir.' }],
    videoTitle: 'SMS ve arama ayarları',
  },
  {
    id: 'marketplace-integrations',
    title: 'Pazaryeri entegrasyonlarını hazırlayın',
    eyebrow: 'Pazaryeri',
    estimate: '12 dk',
    summary: 'Trendyol, Hepsiburada ve diğer pazaryerleri için API bilgileri düzenlenir.',
    details: ['Pazaryeri bazında zorunlu alanlar gösterilir.', 'API anahtarları ve satıcı bilgileri alınır.', 'Bağlantı testi için hazırlık yapılır.'],
    faqs: [{ question: 'Her pazaryerinde alanlar aynı mı?', answer: 'Hayır. Trendyol, Hepsiburada ve diğerleri farklı bilgi isteyebilir.' }],
    videoTitle: 'Pazaryeri bağlantıları',
  },
  {
    id: 'analytics-marketing',
    title: 'Analitik ve pazarlama araçlarını bağlayın',
    eyebrow: 'Analitik',
    estimate: '10 dk',
    summary: 'Google, Facebook, TikTok, ETBİS ve robot.txt gibi takip alanları tek yerde hazırlanır.',
    details: ['Analitik ve reklam kodları alınır.', 'Domain doğrulama bilgileri girilir.', 'ETBİS ve robot.txt alanları kontrol edilir.'],
    faqs: [{ question: 'Kodları sonra ekleyebilir miyim?', answer: 'Evet. Yayına çıktıktan sonra da panelden düzenlenebilir.' }],
    videoTitle: 'Analitik kodları',
  },
  {
    id: 'about-content',
    title: 'Hakkımızda metnini oluşturun',
    eyebrow: 'İçerik',
    estimate: '6 dk',
    summary: 'Marka hikayesi, kısa açıklama ve mağaza güven metinleri hazırlanır.',
    details: ['Kısa ve uzun hakkımızda metinleri girilir.', 'Mağazanın öne çıkan değerleri seçilir.', 'Yayın öncesi metin kontrol edilir.'],
    faqs: [{ question: 'Hazır metin olur mu?', answer: 'Evet. Başlangıç için taslak metin kullanılabilir.' }],
    videoTitle: 'Hakkımızda hazırlığı',
  },
  {
    id: 'customer-service',
    title: 'Müşteri hizmetleri bilgilerini girin',
    eyebrow: 'Destek',
    estimate: '5 dk',
    summary: 'Mail adresi, müşteri hizmetleri telefonu ve WhatsApp hattı müşteriye gösterilecek şekilde hazırlanır.',
    details: ['Destek e-posta adresi girilir.', 'Telefon ve WhatsApp bilgileri kontrol edilir.', 'Çalışma saatleri eklenebilir.'],
    faqs: [{ question: 'WhatsApp zorunlu mu?', answer: 'Hayır. Ancak destek dönüşlerini hızlandırmak için önerilir.' }],
    videoTitle: 'Müşteri hizmetleri',
  },
  {
    id: 'personnel',
    title: 'Personel hesaplarını oluşturun',
    eyebrow: 'Personel',
    estimate: '8 dk',
    summary: 'Paneli kullanacak ekip üyeleri ve yetki seviyeleri hazırlanır.',
    details: ['Personel bilgileri girilir.', 'Departman ve rol seçimi yapılır.', 'Davet veya şifre kurgusu belirlenir.'],
    faqs: [{ question: 'Yetkileri sonra değiştirebilir miyim?', answer: 'Evet. Personel yetkileri panelden tekrar düzenlenebilir.' }],
    videoTitle: 'Personel ekleme',
  },
  {
    id: 'category-menu',
    title: 'Kategori ağacı ve menüleri kurun',
    eyebrow: 'Kategori',
    estimate: '12 dk',
    summary: 'Kategori hiyerarşisi, ana menüler ve görünürlükler mağaza yapısına göre hazırlanır.',
    details: ['Kategori ağacı oluşturulur.', 'Menü başlıkları ve sıralama belirlenir.', 'Mobil ve masaüstü görünüm kontrol edilir.'],
    faqs: [{ question: 'Excel ile kategori alabilir miyim?', answer: 'Evet. Toplu ürün/kategori sürecinde şablon desteklenebilir.' }],
    videoTitle: 'Kategori ve menü',
  },
  {
    id: 'filters',
    title: 'Filtreleri ve sınıf yönetimini hazırlayın',
    eyebrow: 'Filtre',
    estimate: '8 dk',
    summary: 'Müşterilerin ürünleri daha kolay bulması için filtre ve sınıf seçenekleri hazırlanır.',
    details: ['Filtre başlıkları seçilir.', 'Kategoriye göre filtre görünürlüğü ayarlanır.', 'Sıralama ve varsayılan durum belirlenir.'],
    faqs: [{ question: 'Her kategoride aynı filtre mi görünür?', answer: 'Hayır. Kategoriye göre farklı filtre setleri kullanılabilir.' }],
    videoTitle: 'Filtre yönetimi',
  },
  {
    id: 'size-chart',
    title: 'Beden tablosunu ekleyin',
    eyebrow: 'Beden',
    estimate: '7 dk',
    summary: 'Ürün sayfalarında kullanılacak beden tabloları kategori veya marka bazında hazırlanır.',
    details: ['Beden ölçüleri girilir.', 'Varsayılan tablo seçilir.', 'Ürünlere bağlama kuralı belirlenir.'],
    faqs: [{ question: 'Birden fazla beden tablosu olabilir mi?', answer: 'Evet. Kategori, marka veya ürün tipine göre farklı tablolar kullanılabilir.' }],
    videoTitle: 'Beden tablosu',
  },
  {
    id: 'color-grouping',
    title: 'Renk gruplama yapısını kurun',
    eyebrow: 'Renk',
    estimate: '6 dk',
    summary: 'Benzer renkler ve ürün varyantları müşteriye daha temiz gösterilecek şekilde gruplanır.',
    details: ['Ana renkler belirlenir.', 'Alt renk karşılıkları atanır.', 'Varyant görünümü kontrol edilir.'],
    faqs: [{ question: 'Renkleri sonradan değiştirebilir miyim?', answer: 'Evet. Ürün yönetiminde tekrar düzenlenebilir.' }],
    videoTitle: 'Renk gruplama',
  },
  {
    id: 'social-links',
    title: 'Sosyal ağ linklerini ekleyin',
    eyebrow: 'Sosyal',
    estimate: '4 dk',
    summary: 'Instagram, Facebook, TikTok ve diğer sosyal hesaplar mağaza temasına bağlanır.',
    details: ['Sosyal ağ URL adresleri girilir.', 'Tema görünürlükleri kontrol edilir.', 'Boş hesaplar yayında gösterilmez.'],
    faqs: [{ question: 'Tüm sosyal ağlar zorunlu mu?', answer: 'Hayır. Sadece kullandığınız hesapları ekleyebilirsiniz.' }],
    videoTitle: 'Sosyal linkler',
  },
  {
    id: 'stores',
    title: 'Mağazalarım bilgilerini girin',
    eyebrow: 'Mağaza',
    estimate: '8 dk',
    summary: 'Fiziksel mağaza, depo veya teslim noktaları müşteriye gösterilecek şekilde hazırlanır.',
    details: ['Mağaza adresleri girilir.', 'Harita ve iletişim bilgileri eklenir.', 'Çalışma saatleri tanımlanır.'],
    faqs: [{ question: 'Fiziksel mağazam yoksa ne olur?', answer: 'Bu adımı boş bırakıp daha sonra ekleyebilirsiniz.' }],
    videoTitle: 'Mağaza lokasyonları',
  },
  {
    id: 'member-import',
    title: 'Üye datası ekleyin',
    eyebrow: 'Üye',
    estimate: '10 dk',
    summary: 'Mevcut üyeler şablon dosya üzerinden içeri alınacak şekilde hazırlanır.',
    details: ['Şablon dosya indirilir.', 'Üye datası kontrol edilir.', 'Yükleme sonrası hatalı satırlar raporlanır.'],
    faqs: [{ question: 'Şablonsuz dosya yüklenir mi?', answer: 'Şimdilik önerilen akış şablon dosya üzerinden ilerlemektir.' }],
    videoTitle: 'Üye içe aktarma',
  },
  {
    id: 'invoice-cargo-templates',
    title: 'Fatura ve kargo şablonlarını seçin',
    eyebrow: 'Şablon',
    estimate: '7 dk',
    summary: 'Fatura ve kargo çıktı formatları için hazır şablonlar seçilir veya düzenlenir.',
    details: ['10x10 ve 10x20 gibi hazır şablonlar önerilir.', 'Logo ve adres alanları kontrol edilir.', 'Önizleme ile çıktı düzeni incelenir.'],
    faqs: [{ question: 'Şablonu sonra değiştirebilir miyim?', answer: 'Evet. Operasyon ihtiyacına göre farklı şablon seçilebilir.' }],
    videoTitle: 'Çıktı şablonları',
  },
  {
    id: 'domain-routing',
    title: 'Domain yönlendirmesini hazırlayın',
    eyebrow: 'Domain',
    estimate: '9 dk',
    summary: 'Alan adı, DNS, SSL ve yönlendirme kontrolleri yayın öncesi hazırlanır.',
    details: ['Alan adı bilgisi girilir.', 'DNS kayıtları kontrol edilir.', 'SSL ve yönlendirme durumu izlenir.'],
    faqs: [{ question: 'Domaini şimdi almak zorunda mıyım?', answer: 'Hayır. Demo domain ile devam edip sonra kendi alan adınızı bağlayabilirsiniz.' }],
    videoTitle: 'Domain yönlendirme',
  },
  {
    id: 'product-excel-import',
    title: 'Excel ile toplu ürün yükleyin',
    eyebrow: 'Ürün',
    estimate: '15 dk',
    summary: 'Toplu ürün yükleme için şablon, dosya kontrolü ve önizleme akışı hazırlanır.',
    details: ['Ürün şablonu indirilir.', 'Kolonlar ve zorunlu alanlar kontrol edilir.', 'Yükleme öncesi önizleme yapılır.'],
    faqs: [{ question: 'Hatalı satırlar ne olacak?', answer: 'Yükleme öncesi raporlanır ve düzeltmeniz istenir.' }],
    videoTitle: 'Excel ürün yükleme',
  },
  {
    id: 'product-manual',
    title: 'Manuel ürün yükleyin',
    eyebrow: 'Ürün',
    estimate: '10 dk',
    summary: 'Tekil ürün girişi için temel ürün, görsel, fiyat ve stok alanları hazırlanır.',
    details: ['Ürün bilgileri girilir.', 'Fiyat ve stok kontrol edilir.', 'Görsel ve kategori eşlemesi yapılır.'],
    faqs: [{ question: 'Varyantlı ürün ekleyebilir miyim?', answer: 'Evet. Renk, beden ve diğer varyant seçenekleri ayrıca tanımlanabilir.' }],
    videoTitle: 'Manuel ürün ekleme',
  },
  {
    id: 'model-definition',
    title: 'Manken tanımlamasını yapın',
    eyebrow: 'Manken',
    estimate: '6 dk',
    summary: 'Ürün görsellerinde kullanılan manken bilgileri ve ölçüleri tema alanlarında kullanılmak üzere hazırlanır.',
    details: ['Manken adı ve ölçüleri girilir.', 'Kategori veya ürün grubu eşlemesi yapılır.', 'Ürün sayfasındaki gösterim kontrol edilir.'],
    faqs: [{ question: 'Manken bilgisi zorunlu mu?', answer: 'Hayır. Moda ve tekstil kategorilerinde faydalı bir yardımcı bilgidir.' }],
    videoTitle: 'Manken bilgileri',
  },
  {
    id: 'returns',
    title: 'İptal ve iade taleplerini hazırlayın',
    eyebrow: 'İade',
    estimate: '8 dk',
    summary: 'Müşterinin iptal, iade ve değişim talepleri için kurallar ve mesajlar hazırlanır.',
    details: ['İade süreleri belirlenir.', 'İptal ve değişim seçenekleri seçilir.', 'Müşteri bilgilendirme metinleri hazırlanır.'],
    faqs: [{ question: 'İade süresi kaç gün olmalı?', answer: 'Yasal süreç ve firma politikanıza göre belirlenmeli; varsayılan öneriler düzenlenebilir.' }],
    videoTitle: 'İptal ve iade',
  },
]

const SETUP_DETAIL_PANELS: Record<string, SetupDetailPanel> = {
  'content-pages': {
    intro: 'Firma bilgilerinden otomatik doldurulabilecek sözleşme alanları başlangıçta hazır gelir; değişmesi gereken yerleri manuel düzenleyebilirsiniz.',
    cards: [
      { title: 'Mesafeli Satış Sözleşmesi', description: 'Firma, tüketici ve sipariş alanları kontrol edilir.', icon: 'fa-file-contract' },
      { title: 'Gizlilik Politikası', description: 'gizlilik-politikasi linki için temel metin hazırlanır.', icon: 'fa-shield-check' },
      { title: 'KVKK ve Açık Rıza', description: 'Aydınlatma ve onay metinleri düzenlenir.', icon: 'fa-lock-keyhole' },
    ],
    groups: [
      { title: 'Sözleşme bilgileri', fields: [
        { label: 'Firma unvanı', placeholder: 'Başvurudaki firma bilgisiyle dolacak' },
        { label: 'Yetkili kişi', placeholder: 'Yetkili ad soyad' },
        { label: 'Sözleşme e-posta adresi', placeholder: 'sozlesme@firma.com' },
        { label: 'Gizlilik politikası slug', placeholder: 'gizlilik-politikasi' },
      ] },
      { title: 'Otomatik metin taslağı', textarea: { label: 'Sözleşme içeriği', value: 'Sistem tarafında otomatik oluşturuldu. Değiştirmek istediğiniz alanlara manuel müdahale edebilirsiniz.' } },
    ],
  },
  'faq-library': {
    intro: 'Kütüphaneden gelen sorular başlangıç listesi olarak kullanılır; firma kendi yanıtlarını düzenleyebilir.',
    cards: [
      { title: 'Sipariş', description: 'Sipariş durumu, ödeme ve takip soruları.', icon: 'fa-bag-shopping' },
      { title: 'Teslimat', description: 'Kargo süresi ve teslimat koşulları.', icon: 'fa-truck-fast' },
      { title: 'İade', description: 'İptal, iade ve değişim süreçleri.', icon: 'fa-rotate-left' },
    ],
    groups: [
      { title: 'Öne çıkan sorular', chips: ['Siparişim ne zaman kargoya verilir?', 'İade süreci nasıl ilerler?', 'Kapıda ödeme var mı?', 'Üyelik zorunlu mu?'] },
      { title: 'Manuel düzenleme', textarea: { label: 'Soru ve cevap notları', placeholder: 'Firma özelindeki ek soruları buraya yazın' } },
    ],
  },
  'sms-call-settings': {
    intro: 'Telsam SMS ve robot arama bilgileri ile gönderilecek mesaj senaryoları hazırlanır.',
    cards: [
      { title: 'Telsam SMS', description: 'API kullanıcı bilgileri ve başlık kontrol edilir.', icon: 'fa-message-sms' },
      { title: 'Robot Arama', description: 'Arama sağlayıcı bilgileri hazırlanır.', icon: 'fa-phone-volume' },
      { title: 'Mesaj Kuralları', description: 'Sipariş, kargo ve üyelik mesajları seçilir.', icon: 'fa-list-check' },
    ],
    groups: [
      { title: 'Telsam bilgileri', fields: [
        { label: 'SMS kullanıcı adı', placeholder: 'Telsam kullanıcı adı' },
        { label: 'SMS API şifresi', placeholder: 'API şifresi' },
        { label: 'SMS başlığı', placeholder: 'Marka başlığı' },
        { label: 'Robot arama token', placeholder: 'Token veya API anahtarı' },
      ] },
      { title: 'Gönderilecek SMS tipleri', chips: ['Sipariş alındı', 'Ödeme bekleniyor', 'Kargoya verildi', 'Teslim edildi', 'Şifre yenileme'] },
    ],
  },
  'marketplace-integrations': {
    intro: 'Pazaryeri bazında gerekli API alanları farklı olabilir; burada bağlantı ön hazırlığı yapılır.',
    cards: [
      { title: 'Trendyol', description: 'API KEY, API SECRET, TOKEN, Marka ID, Satıcı ID.', icon: 'fa-store' },
      { title: 'Hepsiburada', description: 'Entegratör firma, Merchant ID, API Secret, Token.', icon: 'fa-shop' },
      { title: 'Diğerleri', description: 'N11, Amazon ve ek pazaryerleri için alanlar.', icon: 'fa-grid-2-plus' },
    ],
    groups: [
      { title: 'Trendyol bilgileri', fields: [
        { label: 'API KEY', placeholder: 'Trendyol API KEY' },
        { label: 'API SECRET', placeholder: 'Trendyol API SECRET' },
        { label: 'TOKEN', placeholder: 'Token' },
        { label: 'Marka ID', placeholder: 'Marka ID' },
        { label: 'Satıcı ID', placeholder: 'Satıcı ID' },
      ] },
      { title: 'Hepsiburada bilgileri', fields: [
        { label: 'Entegratör firma', placeholder: 'Firma adı' },
        { label: 'Merchant ID', placeholder: 'Merchant ID' },
        { label: 'API Secret', placeholder: 'API Secret' },
        { label: 'Token', placeholder: 'Token' },
      ] },
    ],
  },
  'analytics-marketing': {
    intro: 'Analitik, reklam ve doğrulama kodları tek kontrol ekranında hazırlanır.',
    groups: [
      { title: 'Google ve ETBİS', fields: [
        { label: 'Analitik kodu', placeholder: 'G-XXXXXXXXXX' },
        { label: 'Tag manager kodu', placeholder: 'GTM-XXXXXXX' },
        { label: 'Google App ID', placeholder: 'Google App ID' },
        { label: 'ETBİS kodu', placeholder: 'ETBİS kodu' },
        { label: 'ETBİS ID', placeholder: 'ETBİS ID' },
        { label: 'ETBİS link', placeholder: 'https://...' },
      ] },
      { title: 'Meta ve TikTok', fields: [
        { label: 'Facebook pixel ID', placeholder: 'Pixel ID' },
        { label: 'Facebook domain doğrulama', placeholder: 'Doğrulama kodu' },
        { label: 'Facebook conversion API version', placeholder: 'v20.0' },
        { label: 'Conversion API Pixel ID', placeholder: 'Pixel ID' },
        { label: 'Conversion API Token', placeholder: 'Token' },
        { label: 'Facebook App ID', placeholder: 'App ID' },
        { label: 'TikTok pixel ID', placeholder: 'TikTok pixel ID' },
      ] },
      { title: 'Robot TXT', textarea: { label: 'Robot TXT', placeholder: 'User-agent: *\nAllow: /' } },
    ],
  },
  'about-content': {
    intro: 'Mağazanızın güven veren marka hikayesini kısa ve uzun metinlerle hazırlayın.',
    groups: [
      { title: 'Hakkımızda', textarea: { label: 'Hakkımızda metni', placeholder: 'Markanızın hikayesini, hizmet alanını ve müşteriye vaadini yazın.' } },
      { title: 'Öne çıkan değerler', chips: ['Hızlı teslimat', 'Güvenli ödeme', 'Kolay iade', 'Müşteri memnuniyeti'] },
    ],
  },
  'customer-service': {
    intro: 'Müşterinin size ulaşacağı kanallar tema ve iletişim sayfalarında kullanılacak şekilde hazırlanır.',
    groups: [
      { title: 'İletişim bilgileri', fields: [
        { label: 'Mail adresi', placeholder: 'destek@firma.com', type: 'email' },
        { label: 'Müşteri hizmetleri telefonu', placeholder: '0850 ___ __ __' },
        { label: 'WhatsApp numarası', placeholder: '+90 5__ ___ __ __' },
        { label: 'Çalışma saatleri', placeholder: 'Hafta içi 09:00 - 18:00' },
      ] },
    ],
  },
  'personnel': {
    intro: 'Paneli kullanacak ekip üyeleri için temel hesap ve departman hazırlığı yapılır.',
    groups: [
      { title: 'Personel bilgileri', fields: [
        { label: 'Ad soyad', placeholder: 'Personel adı soyadı' },
        { label: 'E-posta', placeholder: 'personel@firma.com', type: 'email' },
        { label: 'Telefon', placeholder: '+90 5__ ___ __ __' },
        { label: 'Departman', placeholder: 'Örn. Operasyon' },
        { label: 'Yetki grubu', placeholder: 'Örn. Sipariş yönetimi' },
      ] },
      { title: 'Başlangıç yetkileri', chips: ['Sipariş', 'Ürün', 'Kargo', 'Müşteri', 'Rapor'] },
    ],
  },
  'category-menu': {
    intro: 'Kategori ağacı ve menüler mağazanın ilk navigasyon yapısını oluşturur.',
    groups: [
      { title: 'Ana kategoriler', chips: ['Kadın', 'Erkek', 'Çocuk', 'Aksesuar', 'Kampanya'] },
      { title: 'Menü bilgileri', fields: [
        { label: 'Ana menü adı', placeholder: 'Ana Menü' },
        { label: 'Mobil menü adı', placeholder: 'Mobil Menü' },
        { label: 'Varsayılan kategori sıralaması', placeholder: 'Sıra numarası veya alfabetik' },
      ] },
    ],
  },
  'filters': {
    intro: 'Filtreler müşterinin ürünleri hızlı bulmasını sağlar; kategoriye göre farklı setler hazırlanabilir.',
    groups: [
      { title: 'Filtre seçenekleri', chips: ['Beden', 'Renk', 'Marka', 'Fiyat', 'Materyal', 'Kalıp', 'Cinsiyet'] },
      { title: 'Sınıf yönetimi', fields: [
        { label: 'Sınıf adı', placeholder: 'Örn. Giyim filtreleri' },
        { label: 'Bağlı kategori', placeholder: 'Örn. Giyim - Tekstil' },
      ] },
    ],
  },
  'size-chart': {
    intro: 'Beden tabloları ürün sayfasında satın alma kararını kolaylaştırır.',
    groups: [
      { title: 'Tablo bilgileri', fields: [
        { label: 'Tablo adı', placeholder: 'Kadın üst giyim beden tablosu' },
        { label: 'Kategori', placeholder: 'Giyim - Tekstil' },
        { label: 'Ölçü birimi', placeholder: 'cm' },
      ] },
      { title: 'Ölçü satırları', chips: ['XS', 'S', 'M', 'L', 'XL', 'XXL'] },
    ],
  },
  'color-grouping': {
    intro: 'Renk grupları ürün listeleme ve varyant geçişlerinde daha temiz görünüm sağlar.',
    groups: [
      { title: 'Ana renkler', chips: ['Siyah', 'Beyaz', 'Mavi', 'Kırmızı', 'Yeşil', 'Bej'] },
      { title: 'Eşleştirme', fields: [
        { label: 'Alt renk adı', placeholder: 'Örn. Lacivert' },
        { label: 'Bağlı ana renk', placeholder: 'Mavi' },
        { label: 'Renk kodu', placeholder: '#0f172a' },
      ] },
    ],
  },
  'social-links': {
    intro: 'Sosyal ağ bağlantıları tema footer ve iletişim alanlarında kullanılır.',
    groups: [
      { title: 'Sosyal hesaplar', fields: [
        { label: 'Instagram', placeholder: 'https://instagram.com/...' },
        { label: 'Facebook', placeholder: 'https://facebook.com/...' },
        { label: 'TikTok', placeholder: 'https://tiktok.com/@...' },
        { label: 'YouTube', placeholder: 'https://youtube.com/...' },
        { label: 'X / Twitter', placeholder: 'https://x.com/...' },
      ] },
    ],
  },
  'stores': {
    intro: 'Fiziksel mağaza veya teslim noktaları müşteriye harita ve iletişim bilgisiyle gösterilir.',
    groups: [
      { title: 'Mağaza bilgisi', fields: [
        { label: 'Mağaza adı', placeholder: 'Merkez mağaza' },
        { label: 'Telefon', placeholder: '0212 ___ __ __' },
        { label: 'Adres', placeholder: 'Mağaza adresi', wide: true },
        { label: 'Harita linki', placeholder: 'Google Maps linki', wide: true },
      ] },
    ],
  },
  'member-import': {
    intro: 'Üye datası standart şablonla alınır; yüklemeden önce kolon kontrolü yapılır.',
    cards: [
      { title: 'Şablonu indir', description: 'Standart üye içe aktarma dosyasını alın.', icon: 'fa-file-arrow-down' },
      { title: 'Dosyayı yükle', description: 'Excel dosyasını ön kontrol için seçin.', icon: 'fa-file-arrow-up' },
      { title: 'Hata raporu', description: 'Eksik kolon ve hatalı satırları görün.', icon: 'fa-file-circle-exclamation' },
    ],
    groups: [
      { title: 'Beklenen kolonlar', chips: ['Ad', 'Soyad', 'E-posta', 'Telefon', 'Doğum tarihi', 'İzin durumu'] },
    ],
  },
  'invoice-cargo-templates': {
    intro: 'Fatura ve kargo çıktıları için hazır şablonlar seçilebilir.',
    cards: [
      { title: '10x10 Kargo', description: 'Standart kargo etiketi.', icon: 'fa-barcode' },
      { title: '10x20 Kargo', description: 'Geniş kargo etiketi.', icon: 'fa-barcode-read' },
      { title: 'Fatura A4', description: 'Standart fatura çıktısı.', icon: 'fa-file-invoice' },
    ],
    groups: [
      { title: 'Şablon bilgileri', fields: [
        { label: 'Varsayılan kargo şablonu', placeholder: '10x10' },
        { label: 'Varsayılan fatura şablonu', placeholder: 'A4' },
      ] },
    ],
  },
  'domain-routing': {
    intro: 'Kendi domaininiz bağlanmadan önce DNS, SSL ve yönlendirme kontrolleri hazırlanır.',
    groups: [
      { title: 'Domain bilgileri', fields: [
        { label: 'Alan adı', placeholder: 'www.firma.com' },
        { label: 'CNAME / A kaydı', placeholder: 'DNS hedefi' },
        { label: 'SSL durumu', placeholder: 'Otomatik kontrol edilecek' },
      ] },
      { title: 'Kontrol listesi', chips: ['DNS yönlendi', 'SSL alındı', 'www yönlendirmesi', 'Ana domain yönlendirmesi'] },
    ],
  },
  'product-excel-import': {
    intro: 'Toplu ürün yükleme öncesinde şablon, kolon ve önizleme akışı hazırlanır.',
    cards: [
      { title: 'Ürün şablonu', description: 'Excel şablonunu indirin.', icon: 'fa-file-spreadsheet' },
      { title: 'Görsel eşleme', description: 'Ürün görselleri için klasör/URL kuralı.', icon: 'fa-images' },
      { title: 'Önizleme', description: 'Yükleme öncesi satır kontrolü.', icon: 'fa-eye' },
    ],
    groups: [
      { title: 'Beklenen kolonlar', chips: ['Ürün adı', 'SKU', 'Barkod', 'Kategori', 'Fiyat', 'Stok', 'Renk', 'Beden'] },
    ],
  },
  'product-manual': {
    intro: 'Tekil ürün ekleme akışı için temel alanlar hazırlanır.',
    groups: [
      { title: 'Ürün bilgileri', fields: [
        { label: 'Ürün adı', placeholder: 'Ürün adı' },
        { label: 'SKU', placeholder: 'Stok kodu' },
        { label: 'Barkod', placeholder: 'Barkod' },
        { label: 'Kategori', placeholder: 'Kategori seçimi' },
        { label: 'Satış fiyatı', placeholder: '0,00 TL' },
        { label: 'Stok', placeholder: '0' },
      ] },
      { title: 'Açıklama', textarea: { label: 'Ürün açıklaması', placeholder: 'Ürünün detaylı açıklamasını yazın' } },
    ],
  },
  'model-definition': {
    intro: 'Manken bilgileri özellikle tekstil ürünlerinde ölçü ve görünüm güveni sağlar.',
    groups: [
      { title: 'Manken bilgileri', fields: [
        { label: 'Manken adı', placeholder: 'Örn. Elif' },
        { label: 'Boy', placeholder: '175 cm' },
        { label: 'Kilo', placeholder: '58 kg' },
        { label: 'Giydiği beden', placeholder: 'M' },
        { label: 'Bağlı kategori', placeholder: 'Kadın üst giyim' },
      ] },
    ],
  },
  'returns': {
    intro: 'İptal, iade ve değişim talepleri için politika ve müşteri mesajları hazırlanır.',
    groups: [
      { title: 'İade kuralları', fields: [
        { label: 'İade süresi', placeholder: '14 gün' },
        { label: 'Değişim süresi', placeholder: '30 gün' },
        { label: 'İade kargo firması', placeholder: 'Anlaşmalı kargo' },
      ] },
      { title: 'Müşteri bilgilendirme metni', textarea: { label: 'İade açıklaması', placeholder: 'İade ve değişim koşullarınızı açıklayın.' } },
    ],
  },
}

type CommandSearchItem = {
  id: string
  type?: string
  group: string
  title: string
  subtitle: string
  icon: string
  url?: string
  keywords?: string[]
  adminOnly?: boolean
  answer?: string
  sourceTitle?: string
  sourceType?: string
  moduleId?: number
  moduleActive?: boolean
  settingsUrl?: string
  marketUrl?: string
  managementUrl?: string
  managementTitle?: string
}

type CommandSearchApiItem = {
  id?: unknown
  type?: unknown
  group?: unknown
  title?: unknown
  subtitle?: unknown
  answer?: unknown
  sourceTitle?: unknown
  sourceType?: unknown
  icon?: unknown
  url?: unknown
  moduleId?: unknown
  moduleActive?: unknown
  settingsUrl?: unknown
  marketUrl?: unknown
  managementUrl?: unknown
  managementTitle?: unknown
}

type TopBarUserWithStoreName = {
  firm_store_name?: string | null
  firm?: {
    name?: string | null
  } | null
}

const COMMAND_SEARCH_ALLOWED_GROUPS = new Set([
  'Sipariş',
  'Ürün',
  'Müşteri',
  'Fatura',
  'Yardım',
  'Menü ve Ayarlar',
])

const normalizeCommandSearchGroup = (group: unknown) => {
  const value = String(group || '').trim()
  return COMMAND_SEARCH_ALLOWED_GROUPS.has(value) ? value : 'Yardım'
}

const isBlockedCommandSearchItem = (item: CommandSearchApiItem) => {
  const type = String(item?.type || '').toLowerCase()
  const sourceType = String(item?.sourceType || '').toLowerCase()
  const url = String(item?.url || '').toLowerCase()
  return type === 'personnel' || sourceType === 'personel' || url.includes('/personnel/')
}

const SETUP_GUIDE_BASE_STEPS: SetupGuideStep[] = [
  {
    id: 'welcome',
    title: 'Kuruluma hoş geldiniz',
    eyebrow: 'Başlangıç',
    estimate: '2 dk',
    summary: 'Bu bölümde mağazanızın kurulum sürecinde hangi bilgilerin isteneceğini ve ilerlemenin nasıl takip edileceğini anlatacağız.',
    details: [
      'Mağazanızı yayına hazırlamak için gereken başlıkları sırayla birlikte kontrol edeceğiz.',
      'Hazır olmayan bilgiler varsa daha sonra aynı yerden devam edebilirsiniz.',
      'Her bölümde ne yapmanız gerektiğini kısa açıklamalar ve örneklerle göreceksiniz.',
    ],
    faqs: [
      { question: 'Bu kurulum tek seferde bitmek zorunda mı?', answer: 'Hayır. Her adım ayrı düşünülür; hazırlık gereken noktaları daha sonra tamamlayabilirsiniz.' },
      { question: 'Yanlış bilgi girersem ne olur?', answer: 'Yayına almadan önce tüm bilgiler tekrar kontrol edilir. Gerekirse önceki bölümlere dönüp düzenleme yapabilirsiniz.' },
    ],
    videoTitle: 'Kurulum ekranına kısa bakış',
  },
  {
    id: 'company-info',
    title: 'Firma bilgilerinizi tanıyalım',
    eyebrow: 'Firma Bilgileri',
    estimate: '6 dk',
    summary: 'Bu bölümde firmanızı doğru tanımak için resmi firma bilgilerinizi, yetkili kişi bilgilerini, adresinizi ve iletişim kanallarınızı birlikte kontrol edeceğiz.',
    details: [
      'Resmi firma adı, vergi bilgileri ve ticari unvan gibi temel bilgileriniz alınacak.',
      'Kurulum sürecinde iletişim kurulacak yetkili kişinin adı, görevi, e-posta adresi ve telefonu kontrol edilecek.',
      'Fatura, sözleşme ve iletişim süreçlerinde kullanılacak adres ve firma iletişim bilgileri tamamlanacak.',
      'Eksik veya emin olmadığınız bilgiler varsa bu bölümde işaretleyip daha sonra tamamlayabileceksiniz.',
    ],
    faqs: [
      { question: 'Resmi firma adı ile mağaza adı farklı olabilir mi?', answer: 'Evet. Resmi firma adı fatura ve sözleşme süreçlerinde, mağaza adı ise müşterilerinizin göreceği alanlarda kullanılabilir.' },
      { question: 'Yetkili kişi bilgileri neden gerekli?', answer: 'Kurulum sırasında onay, eksik bilgi ve destek konularında doğru kişiyle hızlıca iletişim kurabilmemiz için gereklidir.' },
      { question: 'Adres bilgilerini daha sonra değiştirebilir miyim?', answer: 'Evet. Ancak fatura, sözleşme veya teslimat süreçlerini etkileyen değişikliklerde bilgileri tekrar kontrol etmenizi isteriz.' },
    ],
    videoTitle: 'Firma bilgileri nasıl hazırlanır?',
  },
  {
    id: 'visual-assets',
    title: 'Logo ve görsel kimliği ekleyin',
    eyebrow: 'Görsel Hazırlık',
    estimate: '6 dk',
    summary: 'Logo, favicon, mobil logo ve temel marka görselleri kullanıcıyı yormadan tek bir kontrol alanında yönetilecek.',
    details: [
      'Yüklenen görseller için önerilen ölçüler ve dosya türleri alanın yanında anlatılacak.',
      'Eksik görsellerde geçici önizleme kullanılacak; kurulum tamamen durmayacak.',
      'Her görselin web sitesinde nerede görüneceği küçük örneklerle açıklanacak.',
    ],
    faqs: [
      { question: 'SVG logo yüklemek zorunda mıyım?', answer: 'Zorunlu değil. Ancak keskin görünüm için SVG veya yüksek çözünürlüklü PNG önerilecek.' },
      { question: 'Mobil logo ayrı mı olmalı?', answer: 'Zorunlu değil. Gerekirse masaüstü logo mobilde de kullanılabilir.' },
    ],
    videoTitle: 'Logo yükleme ve önizleme',
  },
  {
    id: 'payments',
    title: 'Ödeme seçeneklerini hazırlayın',
    eyebrow: 'Ödeme',
    estimate: '8 dk',
    summary: 'Kredi kartı, havale ve kapıda ödeme gibi seçenekler kullanıcıya iş diliyle anlatılacak; hangi seçeneğin müşteriye nasıl görüneceği gösterilecek.',
    details: [
      'Ödeme seçenekleri aktif edilmeden önce test ve onay durumları açıkça belirtilecek.',
      'Komisyon, taksit ve limit gibi konular sade bilgi kartlarıyla açıklanacak.',
      'Eksik entegrasyon bilgisi varsa kullanıcı doğru ayar ekranına yönlendirilecek.',
    ],
    faqs: [
      { question: 'Ödeme seçeneği aktif edince hemen yayına çıkar mı?', answer: 'Hayır. Yeni kurulumda test ve son onay adımları tamamlanmadan müşteriye açılmayacak.' },
      { question: 'Taksit oranlarını bu ekranda mı gireceğim?', answer: 'Bu adımda genel hazırlık yapılacak; ayrıntılı oranlar ilgili ödeme ayarına yönlendirilerek yönetilecek.' },
    ],
    videoTitle: 'Ödeme ayarları hazırlığı',
  },
  {
    id: 'cargo',
    title: 'Kargo ve teslimat yapısını seçin',
    eyebrow: 'Teslimat',
    estimate: '7 dk',
    summary: 'Kargo firmaları, teslimat bölgeleri ve ücret seçenekleri mağazanızın çalışma düzenine göre adım adım seçilecek.',
    details: [
      'Hangi kargo firmasının hangi mağazada kullanılacağı sade bir seçimle belirlenecek.',
      'Ücretsiz kargo limiti ve özel teslimat kuralları örneklerle anlatılacak.',
      'Kargo entegrasyonu test gerektiriyorsa kullanıcıya beklenen sonuç gösterilecek.',
    ],
    faqs: [
      { question: 'Birden fazla kargo firması seçebilir miyim?', answer: 'Evet. Mağaza ihtiyacına göre birden fazla kargo firması tanımlanabilir.' },
      { question: 'Ücretsiz kargo limiti zorunlu mu?', answer: 'Hayır. Dilerseniz sabit ücret, barem veya kampanya bazlı kargo mantığı kullanılabilir.' },
    ],
    videoTitle: 'Kargo seçimi ve teslimat kuralları',
  },
  {
    id: 'content-pages',
    title: 'Yasal ve bilgilendirme sayfalarını tamamlayın',
    eyebrow: 'İçerikler',
    estimate: '10 dk',
    summary: 'Hakkımızda, çerez politikası, KVKK ve satış sözleşmesi gibi sayfalar kontrol listesi olarak gösterilecek.',
    details: [
      'Her sayfanın müşteriye neden gösterildiği kısa açıklamayla anlatılacak.',
      'Eksik metinler ve tamamlanması gereken alanlar ayrı işaretlenecek.',
      'Yayın öncesi son okuma adımıyla kritik içerikler tekrar kontrol edilecek.',
    ],
    faqs: [
      { question: 'Hazır metin kullanabilir miyim?', answer: 'Evet, ancak yasal metinlerin firma bilgilerinize uygunluğu mutlaka kontrol edilmeli.' },
      { question: 'Eksik sayfa varsa kurulum durur mu?', answer: 'Yayına çıkış aşamasında zorunlu sayfalar tamamlanmadan onay verilmeyecek.' },
    ],
    videoTitle: 'Bilgilendirme sayfaları kontrolü',
  },
  {
    id: 'review',
    title: 'Son kontrol ve yayın hazırlığı',
    eyebrow: 'Onay',
    estimate: '5 dk',
    summary: 'Kurulum tamamlandığında tüm eksikler tek listede toplanacak; kullanıcı neyin hazır, neyin beklemede olduğunu net görecek.',
    details: [
      'Eksik adımlar önem derecesine göre gruplanacak.',
      'Yayına almadan önce mağaza önizlemesi açılacak.',
      'Son onaydan sonra mağazanızın yayına hazırlanması için gerekli kontroller tamamlanacak.',
    ],
    faqs: [
      { question: 'Yayına alma işlemini kim yapacak?', answer: 'Yetkiniz varsa siz onay verebilirsiniz. Gerekirse destek ekibimiz son kontrol için yardımcı olur.' },
      { question: 'Yayından sonra geri dönebilir miyim?', answer: 'Evet. Yayın sonrası değişiklikler kontrollü şekilde tekrar düzenlenebilir.' },
    ],
    videoTitle: 'Yayın öncesi son kontrol',
  },
]

const SETUP_GUIDE_STEPS: SetupGuideStep[] = [
  ...SETUP_GUIDE_BASE_STEPS.filter((step) => step.id !== 'review'),
  ...SETUP_DETAILED_STEPS,
]

const COMMAND_QUICK_ITEMS: CommandSearchItem[] = [
  {
    id: 'orders',
    group: 'Sipariş',
    title: 'Siparişlerde ara',
    subtitle: 'Sipariş no, müşteri, telefon, kargo veya ödeme durumuna ulaşın.',
    icon: 'fa-regular fa-bag-shopping',
    url: '/pages/order/list',
    keywords: ['siparis', 'order', 'paket', 'kargo', 'ödeme', 'odeme'],
  },
  {
    id: 'products',
    group: 'Ürün',
    title: 'Ürünlerde ara',
    subtitle: 'Model kodu, barkod, stok, varyant, renk ve beden bilgilerini bulun.',
    icon: 'fa-regular fa-box-open',
    url: '/pages/product/list',
    keywords: ['urun', 'ürün', 'barkod', 'stok', 'model', 'sku'],
  },
  {
    id: 'members',
    group: 'Müşteri',
    title: 'Müşteri veya üye ara',
    subtitle: 'Ad, e-posta, telefon, cari bakiye ve sipariş geçmişine gidin.',
    icon: 'fa-regular fa-users',
    url: '/pages/member',
    keywords: ['musteri', 'müşteri', 'uye', 'üye', 'cari', 'telefon'],
  },
  {
    id: 'invoices',
    group: 'Fatura',
    title: 'Fatura ve alış faturası ara',
    subtitle: 'Fatura no, alış faturası, tedarikçi ve maliyet kayıtlarına ulaşın.',
    icon: 'fa-regular fa-file-invoice',
    url: '/alis-faturalari',
    keywords: ['fatura', 'alis', 'alış', 'tedarikci', 'tedarikçi', 'maliyet'],
  },
  {
    id: 'system-nav',
    group: 'Menü ve Ayarlar',
    title: 'Menü ve navigasyon ayarları',
    subtitle: 'Panel menülerini, modül bağlantılarını ve görünürlükleri yönetin.',
    icon: 'fa-regular fa-diagram-project',
    url: '/pages/admin/system/nav',
    keywords: ['menu', 'menü', 'nav', 'yetki', 'modul', 'modül'],
    adminOnly: true,
  },
  {
    id: 'help-guide',
    group: 'Yardım',
    title: 'Nasıl yapabilirim?',
    subtitle: 'Menü açıklamaları, menü SSS kayıtları ve aktif modül yardım içeriklerinde ara.',
    icon: 'fa-regular fa-circle-question',
    keywords: ['yardim', 'yardım', 'sss', 'nasil', 'nasıl', 'kategori', 'modul', 'modül'],
  },
]

function normalizeCommandText(value: string) {
  return String(value || '')
    .toLocaleLowerCase('tr-TR')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[ıİ]/g, 'i')
    .trim()
}

const COMMAND_SEARCH_STOP_WORDS = new Set([
  'acaba', 'ama', 'bana', 'ben', 'bir', 'biz', 'bu', 'da', 'de', 'diye',
  'icin', 'için', 'ile', 'mi', 'mı', 'mu', 'mü', 'ne', 'neden', 'nasil',
  'nasıl', 'olan', 'olarak', 've', 'veya', 'ya', 'istiyorum', 'isterim',
])

function stemCommandSearchToken(token: string) {
  const value = normalizeCommandText(token).replace(/[^a-z0-9ğüşöçıİ]/gi, '')
  if (value.length <= 4) return value
  if (value.startsWith('degistir')) return 'degistir'
  const suffixes = [
    'abilirim', 'ebilirim', 'abilir', 'ebilir',
    'larinin', 'lerinin', 'larina', 'lerine', 'larini', 'lerini',
    'sinin', 'sının', 'sunun', 'sünün', 'sine', 'sına', 'suna', 'süne',
    'sini', 'sını', 'sunu', 'sünü', 'inin', 'ının', 'unun', 'ünün',
    'leri', 'ları', 'lara', 'lere', 'lari', 'leri', 'dan', 'den',
    'tan', 'ten', 'nin', 'nın', 'nun', 'nün', 'ini', 'ını', 'unu',
    'ünü', 'de', 'da', 'te', 'ta', 'in', 'ın', 'un', 'ün', 'mek', 'mak',
  ]
  const suffix = suffixes.find((item) => value.endsWith(normalizeCommandText(item)) && value.length - item.length >= 4)
  return suffix ? value.slice(0, -suffix.length) : value
}

function getCommandSearchTokens(value: string) {
  const seen = new Set<string>()
  return normalizeCommandText(value)
    .split(/[^a-z0-9ğüşöçıİ]+/i)
    .map((token) => stemCommandSearchToken(token.trim()))
    .filter((token) => {
      if (token.length < 2 || COMMAND_SEARCH_STOP_WORDS.has(token) || seen.has(token)) return false
      seen.add(token)
      return true
    })
}

function commandSearchMatches(item: CommandSearchItem, normalizedQuery: string, tokens: string[]) {
  const haystack = normalizeCommandText([
    item.title,
    item.subtitle,
    item.answer,
    item.sourceTitle,
    item.sourceType,
    item.group,
    ...(item.keywords || []),
  ].join(' '))
  if (!normalizedQuery) return true
  if (haystack.includes(normalizedQuery)) return true
  if (tokens.length === 0) return false
  const haystackWords = haystack.split(/[^a-z0-9ğüşöçıİ]+/i).filter((word) => word.length >= 3)
  const hitCount = tokens.reduce((count, token) => {
    if (haystack.includes(token)) return count + 1
    return count + (haystackWords.some((word) => word.length >= 4 && token.length >= 4 && (word.includes(token) || token.includes(word))) ? 1 : 0)
  }, 0)
  if (tokens.length >= 2) return hitCount >= 2
  return hitCount >= 1
}

function cleanCommandSearchAnswer(value?: string) {
  return String(value || '')
    .replace(/\s+/g, ' ')
    .replace(/\s+([,.!?])/g, '$1')
    .trim()
}

function scoreCommandSearchItemForAnswer(item: CommandSearchItem, tokens: string[], normalizedQuery: string) {
  const title = normalizeCommandText(item.title)
  const source = normalizeCommandText([item.sourceTitle, item.sourceType, item.group].filter(Boolean).join(' '))
  const body = normalizeCommandText([item.subtitle, item.answer, ...(item.keywords || [])].join(' '))
  let score = 0
  let hits = 0

  if (normalizedQuery && title.includes(normalizedQuery)) score += 80
  if (normalizedQuery && source.includes(normalizedQuery)) score += 45
  if (normalizedQuery && body.includes(normalizedQuery)) score += 25

  tokens.forEach((token) => {
    const hasTitle = title.includes(token)
    const hasSource = source.includes(token)
    const hasBody = body.includes(token)
    if (hasTitle || hasSource || hasBody) hits += 1
    if (hasTitle) score += 18
    if (hasSource) score += 12
    if (hasBody) score += 5
  })

  if (tokens.length >= 2 && hits < Math.min(2, tokens.length)) return 0
  if (item.url) score += 8
  if (String(item.sourceType || '').includes('SSS')) score += 4
  return score
}

function pickCommandSearchAiTarget(query: string, items: CommandSearchItem[]) {
  const tokens = getCommandSearchTokens(query)
  const normalizedQuery = normalizeCommandText(query)
  return items
    .map((item, index) => ({
      item,
      index,
      score: scoreCommandSearchItemForAnswer(item, tokens, normalizedQuery),
    }))
    .sort((a, b) => (b.score - a.score) || (a.index - b.index))[0]?.item || items[0]
}

function buildGroundedCommandSearchAnswer(query: string, items: CommandSearchItem[]) {
  const tokens = getCommandSearchTokens(query)
  const bestItem = pickCommandSearchAiTarget(query, items)
  if (!bestItem) {
    return '**Net cevap:** Bu arama için yeterli yardım kaydı bulamadım.\n\n**Öneri:** Aramayı ekran adı veya yapmak istediğiniz işlemle tekrar deneyin.'
  }

  const bestTitle = cleanCommandSearchAnswer(bestItem.title || bestItem.sourceTitle)
  const bestAnswer = cleanCommandSearchAnswer(bestItem.answer || bestItem.subtitle)

  if (bestItem.moduleId) {
    const managementTitle = cleanCommandSearchAnswer(bestItem.managementTitle || 'Kargo Yönetimi')
    const activeAction = bestItem.managementUrl
      ? `${bestTitle} modülü firmanızda aktif görünüyor. Entegrasyon bilgilerini düzenlemek için modül ayarlarını, operasyonel takip için ${managementTitle} ekranını kullanabilirsiniz.`
      : `${bestTitle} modülü firmanızda aktif görünüyor. Entegrasyon bilgilerini ve çalışma ayarlarını modül ayarları ekranından kontrol edebilirsiniz.`
    const inactiveAction = `${bestTitle} modülü firmanızda aktif görünmüyor. Kullanmak için Uygulama Market üzerinden modülü edinmeniz veya firmanız için aktif hale getirmeniz gerekir.`
    const primaryRoute = bestItem.moduleActive
      ? (bestItem.settingsUrl || bestItem.managementUrl || bestItem.url)
      : (bestItem.marketUrl || bestItem.url)
    const routeText = primaryRoute
      ? `Bu öneriyi açmak için **Enter**'a basın.`
      : `Listeden __${bestTitle}__ sonucunu seçerek detay bilgisini açabilirsiniz.`
    const extraRoute = bestItem.moduleActive && bestItem.managementUrl
      ? `Kargo süreçleri için ayrıca __${managementTitle}__ ekranını da kullanabilirsiniz.`
      : ''

    return [
      `**Anladığım konu:** __${bestTitle}__ bir sistem modülü.`,
      `**Durum:** ${bestItem.moduleActive ? 'Bu modül firmanızda aktif görünüyor.' : 'Bu modül firmanızda aktif görünmüyor.'}`,
      `**Ne yapmalısın?** ${bestItem.moduleActive ? activeAction : inactiveAction}`,
      extraRoute ? `**Ek yönlendirme:** ${extraRoute}` : '',
      `**Yönlendirme:** ${routeText}`,
    ].filter(Boolean).join('\n\n')
  }

  const relatedAnswers = items
    .filter((item) => item.id !== bestItem.id)
    .map((item) => cleanCommandSearchAnswer(item.answer || item.subtitle))
    .filter((answer, index, list) => answer && list.indexOf(answer) === index)
    .filter((answer) => {
      const normalized = normalizeCommandText(answer)
      return tokens.some((token) => normalized.includes(token))
    })
    .slice(0, 2)

  const action = bestAnswer || `${bestTitle} ekranından ilgili ayarı kontrol edebilirsiniz.`
  const routeText = bestItem.url
    ? `Bu sonucu açmak için **Enter**'a basın veya listeden __${bestItem.title}__ sonucunu seçin.`
    : `Listeden __${bestItem.title}__ sonucunu seçerek ilgili modül/ayar bilgisini açabilirsiniz.`

  const lines = [
    `**Gitmen gereken yer:** __${bestTitle}__`,
    `**Ne yapacaksın?** ${action}`,
    relatedAnswers.length ? `**Ek bilgi:** ${relatedAnswers.join(' ')}` : '',
    `**Yönlendirme:** ${routeText}`,
  ].filter(Boolean)

  return lines.join('\n\n')
}

function buildCommandSearchHighlightMap(value: string) {
  let normalized = ''
  const map: number[] = []

  Array.from(String(value || '')).forEach((char, index) => {
    const normalizedChar = normalizeCommandText(char)
    Array.from(normalizedChar).forEach((part) => {
      normalized += part
      map.push(index)
    })
  })

  return { normalized, map }
}

function renderCommandSearchHighlightedText(value: string, query: string) {
  const text = String(value || '')
  const tokens = getCommandSearchTokens(query)
    .map((token) => normalizeCommandText(token))
    .filter((token) => token.length >= 2)

  if (!text || tokens.length === 0) return text

  const { normalized, map } = buildCommandSearchHighlightMap(text)
  const ranges: Array<{ start: number; end: number }> = []

  tokens.forEach((token) => {
    let searchFrom = 0
    while (searchFrom < normalized.length) {
      const matchIndex = normalized.indexOf(token, searchFrom)
      if (matchIndex === -1) break
      const originalStart = map[matchIndex]
      const originalEnd = (map[matchIndex + token.length - 1] ?? originalStart) + 1
      ranges.push({ start: originalStart, end: originalEnd })
      searchFrom = matchIndex + Math.max(token.length, 1)
    }
  })

  if (ranges.length === 0) return text

  const mergedRanges = ranges
    .sort((a, b) => (a.start - b.start) || (a.end - b.end))
    .reduce<Array<{ start: number; end: number }>>((list, range) => {
      const previous = list[list.length - 1]
      if (previous && range.start <= previous.end) {
        previous.end = Math.max(previous.end, range.end)
      } else {
        list.push({ ...range })
      }
      return list
    }, [])

  const nodes: ReactNode[] = []
  let cursor = 0
  mergedRanges.forEach((range, index) => {
    if (range.start > cursor) {
      nodes.push(<span key={`text-${index}-${cursor}`}>{text.slice(cursor, range.start)}</span>)
    }
    nodes.push(
      <mark key={`match-${index}-${range.start}`} className="command-search__match">
        {text.slice(range.start, range.end)}
      </mark>
    )
    cursor = range.end
  })
  if (cursor < text.length) {
    nodes.push(<span key={`text-end-${cursor}`}>{text.slice(cursor)}</span>)
  }

  return nodes
}

function renderCommandSearchRichText(value: string, query = '') {
  const blocks = String(value || '').split(/\n{2,}/).filter(Boolean)
  return blocks.map((block, blockIndex) => {
    const parts = block.split(/(\*\*[^*]+\*\*|__[^_]+__)/g).filter(Boolean)
    return (
      <span key={`${block}-${blockIndex}`} className="command-search__rich-line">
        {parts.map((part, partIndex) => {
          if (part.startsWith('**') && part.endsWith('**')) {
            return <strong key={`${part}-${partIndex}`}>{renderCommandSearchHighlightedText(part.slice(2, -2), query)}</strong>
          }
          if (part.startsWith('__') && part.endsWith('__')) {
            return <u key={`${part}-${partIndex}`}>{renderCommandSearchHighlightedText(part.slice(2, -2), query)}</u>
          }
          return <span key={`${part}-${partIndex}`}>{renderCommandSearchHighlightedText(part, query)}</span>
        })}
      </span>
    )
  })
}

export function TopBar({ dashboardSelector }: TopBarProps) {
  const router = useRouter()
  const { user } = useUser()
  const userStoreInfo = user as TopBarUserWithStoreName | null
  const { mobileSidebarOpen, setMobileSidebarOpen } = useUI()
  const { showToast } = useToast()
  const deferredStartupReady = useDeferredStartup()
  const storeSelector = dashboardSelector ?? <StoreSelector />
  const [notificationOpen, setNotificationOpen] = useState(false)
  const [notificationLoading, setNotificationLoading] = useState(false)
  const [notifications, setNotifications] = useState<NotificationItem[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [storeSettingsOpen, setStoreSettingsOpen] = useState(false)
  const [storeSettingsPinned, setStoreSettingsPinned] = useState(false)
  const [storeSettingsTab, setStoreSettingsTab] = useState<StoreSettingsTab>('json')
  const [storeSettingsSearch, setStoreSettingsSearch] = useState('')
  const [storeSettingsLoading, setStoreSettingsLoading] = useState(false)
  const [storeSettingsResetting, setStoreSettingsResetting] = useState(false)
  const [storeSettingsError, setStoreSettingsError] = useState('')
  const [storeSettings, setStoreSettings] = useState<StoreSettingsResponse | null>(null)
  const [cacheClearing, setCacheClearing] = useState(false)
  const [setupTasksOpen, setSetupTasksOpen] = useState(false)
  const [setupTasksLoading, setSetupTasksLoading] = useState(false)
  const [setupTasksError, setSetupTasksError] = useState('')
  const [setupTasks, setSetupTasks] = useState<SetupTaskItem[]>([])
  const [setupTaskSummary, setSetupTaskSummary] = useState<SetupTaskSummary>({})
  const [setupGuideOpen, setSetupGuideOpen] = useState(false)
  const [setupGuideStepIndex, setSetupGuideStepIndex] = useState(0)
  const [setupCompanyInfo, setSetupCompanyInfo] = useState<SetupCompanyInfoForm>(SETUP_COMPANY_INFO_INITIAL)
  const [setupCompanySubmitted, setSetupCompanySubmitted] = useState(false)
  const [setupCompanyLoadedKey, setSetupCompanyLoadedKey] = useState('')
  const [setupCompanySaving, setSetupCompanySaving] = useState(false)
  const [setupCompletedStepIds, setSetupCompletedStepIds] = useState<Set<string>>(() => new Set())
  const [setupAssetPreviews, setSetupAssetPreviews] = useState<Partial<Record<SetupAssetKey, SetupAssetPreview>>>({})
  const [setupSvgLogoCode, setSetupSvgLogoCode] = useState('')
  const [setupVisualAssetsLoadedKey, setSetupVisualAssetsLoadedKey] = useState('')
  const [setupVisualAssetsSaving, setSetupVisualAssetsSaving] = useState(false)
  const [setupPaymentLibrary, setSetupPaymentLibrary] = useState<SetupPaymentLibraryItem[]>([])
  const [setupPaymentLibraryLoading, setSetupPaymentLibraryLoading] = useState(false)
  const [setupPaymentLibraryError, setSetupPaymentLibraryError] = useState('')
  const [setupPaymentsLoadedKey, setSetupPaymentsLoadedKey] = useState('')
  const [setupSelectedPaymentIds, setSetupSelectedPaymentIds] = useState<number[]>([])
  const [setupPaymentsSaving, setSetupPaymentsSaving] = useState(false)
  const [setupStepTooltip, setSetupStepTooltip] = useState<{
    text: string
    left: number
    top: number
    placement: 'left' | 'center' | 'right'
  } | null>(null)
  const [commandSearchOpen, setCommandSearchOpen] = useState(false)
  const [commandSearchQuery, setCommandSearchQuery] = useState('')
  const [commandSearchActiveIndex, setCommandSearchActiveIndex] = useState(0)
  const [commandSearchHelpItems, setCommandSearchHelpItems] = useState<CommandSearchItem[]>([])
  const [commandSearchHelpLoading, setCommandSearchHelpLoading] = useState(false)
  const [commandSearchAiAnswer, setCommandSearchAiAnswer] = useState('')
  const [commandSearchAiLoading, setCommandSearchAiLoading] = useState(false)
  const notificationRef = useRef<HTMLDivElement | null>(null)
  const commandSearchInputRef = useRef<HTMLInputElement | null>(null)
  const commandSearchAiRequestRef = useRef('')
  const isAdmin = Number(user?.admin || 0) === 1
  const activeSetupStep = SETUP_GUIDE_STEPS[setupGuideStepIndex] || SETUP_GUIDE_STEPS[0]
  const activeSetupDetailPanel = SETUP_DETAIL_PANELS[activeSetupStep.id]
  const setupNextIncompleteIndex = useMemo(() => {
    const nextIndex = SETUP_GUIDE_STEPS.findIndex((step, index) => index > 0 && !setupCompletedStepIds.has(step.id))
    return nextIndex > 0 ? nextIndex : Math.min(1, SETUP_GUIDE_STEPS.length - 1)
  }, [setupCompletedStepIds])
  const setupProgress = Math.round((setupGuideStepIndex / Math.max(1, SETUP_GUIDE_STEPS.length - 1)) * 100)
  const openSetupTaskCount = Number(setupTaskSummary.pending || 0) + Number(setupTaskSummary.in_progress || 0)
  const setupTaskButtonLabel = `${openSetupTaskCount} Görev Bekliyor`
  const displayedSetupTasks = useMemo(() => {
    const doneStatuses = new Set<SetupTaskStatus>(['done', 'not_needed', 'cancelled'])
    return [...setupTasks].sort((a, b) => {
      const aDone = doneStatuses.has(a.status)
      const bDone = doneStatuses.has(b.status)
      if (aDone !== bDone) return aDone ? 1 : -1
      return Number(a.created_at || 0) - Number(b.created_at || 0)
    })
  }, [setupTasks])
  const settingsRows = useMemo(() => {
    const rows = storeSettings?.cache?.list || []
    const query = storeSettingsSearch.trim().toLocaleLowerCase('tr-TR')
    if (!query) return rows
    return rows.filter((row) => {
      const content = String(row.content || '').toLocaleLowerCase('tr-TR')
      const value = String(row.value || '').toLocaleLowerCase('tr-TR')
      const moduleId = String(row.moduleId || '')
      return content.includes(query) || value.includes(query) || moduleId.includes(query)
    })
  }, [storeSettings?.cache?.list, storeSettingsSearch])

  const currentUser = {
    name: user?.name || 'Kullanıcı',
    email: user?.email || '',
    avatar: getInitials(user?.name || ''),
    newVersion: !!user?.firm?.new_version,
    admin: user?.admin,
  }

  const navCommandItems = useMemo<CommandSearchItem[]>(() => {
    const seen = new Set<string>()
    const navItems = Array.isArray(user?.nav) ? user.nav : []
    return navItems
      .filter((item) => item?.url && item?.name)
      .map((item) => {
        const url = String(item.url)
        const key = `${url}::${item.name}`
        if (seen.has(key)) return null
        seen.add(key)
        return {
          id: `nav-${item.id}`,
          group: 'Menü ve Ayarlar',
          title: item.name,
          subtitle: item.desc || `${item.platform || 'Panel'} menüsünü aç`,
          icon: item.icon || 'fa-regular fa-arrow-up-right-from-square',
          url,
          keywords: [item.platform, item.path, item.desc].filter(Boolean) as string[],
        }
      })
      .filter(Boolean) as CommandSearchItem[]
  }, [user?.nav])

  const commandSearchItems = useMemo<CommandSearchItem[]>(() => {
    const quickItems = COMMAND_QUICK_ITEMS.filter((item) => !item.adminOnly || isAdmin)
    return [...quickItems, ...navCommandItems]
  }, [isAdmin, navCommandItems])

  const filteredCommandSearchItems = useMemo(() => {
    const query = normalizeCommandText(commandSearchQuery)
    const queryTokens = getCommandSearchTokens(commandSearchQuery)
    const localItems = commandSearchItems.filter((item) => !item.adminOnly || isAdmin)
    const rawQuery = commandSearchQuery.trim()
    const isQuestionMode = rawQuery.includes('?') || queryTokens.length >= 3
    const matchingHelpItems = commandSearchHelpItems.filter((item) => commandSearchMatches(item, query, queryTokens))
    const aiTargetItem = matchingHelpItems.length > 0 ? pickCommandSearchAiTarget(rawQuery, matchingHelpItems) : null
    const aiAnswerItem: CommandSearchItem | null = query.length >= 2 && matchingHelpItems.length > 0 && (commandSearchAiLoading || commandSearchAiAnswer)
      ? {
          id: `ai-answer-${query}`,
          type: 'ai-answer',
          group: 'AI Cevabı',
          title: commandSearchAiLoading && !commandSearchAiAnswer ? 'AI cevap hazırlıyor' : 'AI cevabı',
          subtitle: commandSearchAiLoading && !commandSearchAiAnswer
            ? 'Sistem modülü SSS cevaplarını okuyup net bir cevap hazırlıyor.'
            : commandSearchAiAnswer,
          answer: commandSearchAiAnswer,
          icon: commandSearchAiLoading ? 'fa-regular fa-spinner-third' : 'fa-regular fa-sparkles',
          url: aiTargetItem?.url || '',
          sourceTitle: aiTargetItem?.sourceTitle || aiTargetItem?.title || '',
          sourceType: aiTargetItem?.sourceType || '',
          keywords: [rawQuery, commandSearchAiAnswer],
        }
      : null
    const aiHelpItem: CommandSearchItem | null = query.length >= 2 && !aiAnswerItem
      ? {
          id: `ai-assist-${query}`,
          type: 'ai-assist',
          group: 'AI Yardım',
          title: 'AI ASISTAN İLE GÖRÜŞ',
          subtitle: 'Hala yardıma ihtiyacınız varsa farktor asistan ile daha fazlasına ulaşabilirsin.',
          icon: 'fa-regular fa-sparkles',
          keywords: [rawQuery, 'asistan', 'yardım', 'nasıl yapabilirim'],
        }
      : null
    const matchingLocalItems = localItems
      .filter((item) => !commandSearchHelpItems.some((helpItem) => helpItem.url && helpItem.url === item.url))
      .filter((item) => commandSearchMatches(item, query, queryTokens))
    const mergedItems = isQuestionMode
      ? [
          ...(aiAnswerItem ? [aiAnswerItem] : []),
          ...(aiHelpItem && matchingHelpItems.length > 0 ? [aiHelpItem] : []),
          ...matchingHelpItems,
          ...matchingLocalItems,
          ...(aiHelpItem && matchingHelpItems.length === 0 ? [aiHelpItem] : []),
        ]
      : [
          ...matchingHelpItems,
          ...matchingLocalItems,
          ...(aiHelpItem ? [aiHelpItem] : []),
        ]
    const baseItems = query
      ? mergedItems
      : mergedItems

    return baseItems.slice(0, query ? 18 : 18)
  }, [commandSearchAiAnswer, commandSearchAiLoading, commandSearchHelpItems, commandSearchItems, commandSearchQuery, isAdmin])

  useEffect(() => {
    setCommandSearchActiveIndex(0)
  }, [commandSearchQuery, commandSearchOpen])

  useEffect(() => {
    if (!commandSearchOpen) return
    const timer = window.setTimeout(() => commandSearchInputRef.current?.focus(), 20)
    return () => window.clearTimeout(timer)
  }, [commandSearchOpen])

  useEffect(() => {
    if (!commandSearchOpen) return
    const query = commandSearchQuery.trim()
    if (query.length < 2) {
      setCommandSearchHelpItems([])
      setCommandSearchHelpLoading(false)
      return
    }
    const token = getToken()
    if (!token) return
    const controller = new AbortController()
    const timer = window.setTimeout(async () => {
      setCommandSearchHelpLoading(true)
      try {
        const response = await fetch(`${API_BASE}/panel/global-search/help?q=${encodeURIComponent(query)}`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        })
        const data = await response.json().catch(() => null)
        if (!response.ok || !data?.success) throw new Error(data?.message || 'Yardım sonuçları alınamadı')
        const items: CommandSearchApiItem[] = Array.isArray(data.data?.items) ? data.data.items : []
        setCommandSearchHelpItems(items.filter((item) => !isBlockedCommandSearchItem(item)).map((item, index) => ({
          id: String(item.id || `help-${index}`),
          type: String(item.type || ''),
          group: normalizeCommandSearchGroup(item.group),
          title: String(item.title || 'Yardım sonucu'),
          subtitle: String(item.subtitle || item.answer || ''),
          answer: String(item.answer || ''),
          sourceTitle: String(item.sourceTitle || ''),
          sourceType: String(item.sourceType || ''),
          icon: String(item.icon || 'fa-regular fa-circle-question'),
          url: String(item.url || ''),
          moduleId: Number(item.moduleId || 0) || undefined,
          moduleActive: typeof item.moduleActive === 'boolean' ? item.moduleActive : String(item.moduleActive || '').toLowerCase() === 'true',
          settingsUrl: String(item.settingsUrl || ''),
          marketUrl: String(item.marketUrl || ''),
          managementUrl: String(item.managementUrl || ''),
          managementTitle: String(item.managementTitle || ''),
          keywords: [item.sourceTitle, item.sourceType].filter(Boolean).map(String),
        })))
      } catch (error) {
        if ((error as Error)?.name !== 'AbortError') {
          setCommandSearchHelpItems([])
        }
      } finally {
        if (!controller.signal.aborted) setCommandSearchHelpLoading(false)
      }
    }, 220)

    return () => {
      window.clearTimeout(timer)
      controller.abort()
    }
  }, [commandSearchOpen, commandSearchQuery])

  useEffect(() => {
    if (!commandSearchOpen) return
    const query = commandSearchQuery.trim()
    const queryTokens = getCommandSearchTokens(query)
    const isQuestionMode = query.includes('?') || queryTokens.length >= 3
    const matchingHelpItems = commandSearchHelpItems
      .filter((item) => commandSearchMatches(item, normalizeCommandText(query), queryTokens))
      .slice(0, 8)

    if (!isQuestionMode || query.length < 3 || matchingHelpItems.length === 0) {
      commandSearchAiRequestRef.current = ''
      setCommandSearchAiAnswer('')
      setCommandSearchAiLoading(false)
      return
    }

    const requestKey = `${query}::${matchingHelpItems.map((item) => item.id).join(',')}`
    if (commandSearchAiRequestRef.current === requestKey) return
    commandSearchAiRequestRef.current = requestKey
    setCommandSearchAiAnswer('')
    setCommandSearchAiLoading(true)

    const timer = window.setTimeout(() => {
      setCommandSearchAiAnswer(buildGroundedCommandSearchAnswer(query, matchingHelpItems))
      setCommandSearchAiLoading(false)
    }, 180)

    return () => {
      window.clearTimeout(timer)
    }
  }, [commandSearchHelpItems, commandSearchOpen, commandSearchQuery])

  useEffect(() => {
    if (!isAdmin) {
      setCommandSearchOpen(false)
      return
    }
    function handleKeyDown(event: KeyboardEvent) {
      const isCommandK = (event.metaKey || event.ctrlKey) && event.key.toLocaleLowerCase('tr-TR') === 'k'
      if (isCommandK) {
        event.preventDefault()
        setCommandSearchOpen(true)
        return
      }
      if (!commandSearchOpen) return
      if (event.key === 'Escape') {
        event.preventDefault()
        setCommandSearchOpen(false)
        return
      }
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        setCommandSearchActiveIndex((index) => Math.min(filteredCommandSearchItems.length - 1, index + 1))
        return
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault()
        setCommandSearchActiveIndex((index) => Math.max(0, index - 1))
        return
      }
      if (event.key === 'Enter') {
        const item = filteredCommandSearchItems[commandSearchActiveIndex]
        if (item) {
          event.preventDefault()
          handleCommandSearchSelect(item)
        }
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [commandSearchActiveIndex, commandSearchOpen, filteredCommandSearchItems, isAdmin])

  const handleCommandSearchSelect = (item: CommandSearchItem) => {
    if (item.type === 'ai-assist') {
      const query = commandSearchQuery.trim()
      if (query && typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('farktor:ai-search-help', { detail: { query } }))
        setCommandSearchOpen(false)
        setCommandSearchQuery('')
      }
      return
    }
    if (!item.url) return
    setCommandSearchOpen(false)
    setCommandSearchQuery('')
    if (/^https?:\/\//i.test(item.url)) {
      window.location.href = item.url
      return
    }
    router.push(item.url)
  }

  const loadUnreadCount = useCallback(async () => {
    const token = getToken()
    if (!token || !user?.id) {
      setUnreadCount(0)
      return
    }
    try {
      const response = await fetch(`${API_BASE}/user-notifications/unread-count`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      const data = await response.json().catch(() => null)
      if (data?.success) setUnreadCount(Number(data.unread || 0))
    } catch (error) {
      console.error('[TopBar] unread notifications failed:', error)
    }
  }, [user?.id])

  const loadNotifications = useCallback(async () => {
    const token = getToken()
    if (!token || !user?.id) return
    setNotificationLoading(true)
    try {
      const response = await fetch(`${API_BASE}/user-notifications?limit=12`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      const data = await response.json().catch(() => null)
      if (data?.success) {
        setNotifications(Array.isArray(data.items) ? data.items : [])
        setUnreadCount(Number(data.unread || 0))
      }
    } catch (error) {
      console.error('[TopBar] notifications failed:', error)
    } finally {
      setNotificationLoading(false)
    }
  }, [user?.id])

  const loadSetupTasks = useCallback(async () => {
    const token = getToken()
    const firmId = Number(user?.firm_id || 0)
    const storeId = Number(user?.firm_store_id || 0)
    if (!isAdmin || !token || !firmId || !storeId) {
      setSetupTasks([])
      setSetupTaskSummary({})
      return
    }

    setSetupTasksLoading(true)
    setSetupTasksError('')
    try {
      const params = new URLSearchParams({
        firm_id: String(firmId),
        store_id: String(storeId),
      })
      const response = await fetch(`/api/setup/tasks?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      const data = await response.json().catch(() => null)
      if (!response.ok || !data?.success) throw new Error(data?.message || 'Görevler alınamadı.')
      setSetupTasks(Array.isArray(data.data?.tasks) ? data.data.tasks : [])
      setSetupTaskSummary(data.data?.summary && typeof data.data.summary === 'object' ? data.data.summary : {})
    } catch (error) {
      setSetupTasksError(error instanceof Error ? error.message : 'Görevler alınamadı.')
    } finally {
      setSetupTasksLoading(false)
    }
  }, [isAdmin, user?.firm_id, user?.firm_store_id])

  useEffect(() => {
    if (!deferredStartupReady) return
    loadUnreadCount()
    const pollUnreadCount = () => {
      if (isBrowserTabVisible()) loadUnreadCount()
    }
    const handleVisibilityChange = () => {
      if (isBrowserTabVisible()) loadUnreadCount()
    }
    const timer = window.setInterval(pollUnreadCount, NOTIFICATION_UNREAD_POLL_MS)
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [deferredStartupReady, loadUnreadCount])

  useEffect(() => {
    function handleOutside(event: MouseEvent) {
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setNotificationOpen(false)
      }
    }
    document.addEventListener('mousedown', handleOutside)
    return () => document.removeEventListener('mousedown', handleOutside)
  }, [])

  useEffect(() => {
    function handleNotificationRefresh() {
      if (!deferredStartupReady) return
      if (notificationOpen) loadNotifications()
      else loadUnreadCount()
    }
    window.addEventListener('panel:notifications-refresh', handleNotificationRefresh)
    return () => window.removeEventListener('panel:notifications-refresh', handleNotificationRefresh)
  }, [deferredStartupReady, loadNotifications, loadUnreadCount, notificationOpen])

  useEffect(() => {
    if (!isAdmin) {
      setSetupTasksOpen(false)
      setSetupTasks([])
      setSetupTaskSummary({})
      return
    }
    if (!deferredStartupReady) return
    const pollSetupTasks = () => {
      if (isBrowserTabVisible()) loadSetupTasks()
    }
    const timer = window.setInterval(
      pollSetupTasks,
      setupTasksOpen ? SETUP_TASKS_OPEN_POLL_MS : SETUP_TASKS_IDLE_POLL_MS,
    )
    const handler = () => loadSetupTasks()
    const handleVisibilityChange = () => {
      if (setupTasksOpen && isBrowserTabVisible()) loadSetupTasks()
    }
    window.addEventListener('setup-tasks:changed', handler)
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      window.clearInterval(timer)
      window.removeEventListener('setup-tasks:changed', handler)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [deferredStartupReady, isAdmin, loadSetupTasks, setupTasksOpen])

  const handleNotificationToggle = () => {
    setNotificationOpen((open) => {
      if (!open) loadNotifications()
      return !open
    })
  }

  const handleNotificationClick = async (item: NotificationItem) => {
    const token = getToken()
    try {
      if (token) {
        await fetch(`${API_BASE}/user-notifications/${item.id}/click`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        })
      }
    } catch (error) {
      console.error('[TopBar] notification click failed:', error)
    }
    setNotificationOpen(false)
    setNotifications((prev) => prev.map((n) => n.id === item.id ? { ...n, read_at: n.read_at || new Date().toISOString() } : n))
    setUnreadCount((count) => Math.max(0, count - (item.read_at ? 0 : 1)))
    const url = String(item.data?.url || '')
    if (url) {
      if (/^https?:\/\//i.test(url)) window.location.href = url
      else router.push(url)
    }
  }

  const handleReadAll = async () => {
    const token = getToken()
    if (!token) return
    try {
      await fetch(`${API_BASE}/user-notifications/read-all`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      })
      setNotifications((prev) => prev.map((item) => ({ ...item, read_at: item.read_at || new Date().toISOString() })))
      setUnreadCount(0)
    } catch (error) {
      console.error('[TopBar] read all notifications failed:', error)
    }
  }

  const loadStoreSettings = useCallback(async () => {
    const token = getToken()
    if (!token || !isAdmin) return
    setStoreSettingsLoading(true)
    setStoreSettingsError('')
    try {
      const params = new URLSearchParams()
      if (user?.firm_store_id) params.set('storeId', String(user.firm_store_id))
      const response = await fetch(`${API_BASE}/panel/store-settings-cache?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      const data = await response.json().catch(() => null)
      if (!response.ok || !data?.success) throw new Error(data?.message || 'Mağaza ayarları alınamadı')
      setStoreSettings(data.data || null)
    } catch (error) {
      setStoreSettingsError(error instanceof Error ? error.message : 'Mağaza ayarları alınamadı')
      setStoreSettings(null)
    } finally {
      setStoreSettingsLoading(false)
    }
  }, [isAdmin, user?.firm_store_id])

  useEffect(() => {
    if (!isAdmin || typeof window === 'undefined') return
    const pinned = localStorage.getItem(STORE_SETTINGS_PINNED_KEY) === '1'
    setStoreSettingsPinned(pinned)
    if (pinned) setStoreSettingsOpen(true)
  }, [isAdmin])

  useEffect(() => {
    if (!isAdmin || !storeSettingsOpen) return
    loadStoreSettings()
  }, [isAdmin, loadStoreSettings, storeSettingsOpen])

  useEffect(() => {
    if (!setupGuideOpen) return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [setupGuideOpen])

  useEffect(() => {
    if (!setupGuideOpen) setSetupStepTooltip(null)
  }, [setupGuideOpen])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const openSetupGuide = () => {
      setSetupGuideStepIndex(setupNextIncompleteIndex)
      setSetupGuideOpen(true)
    }
    window.addEventListener('panel:setup-guide-open', openSetupGuide)
    return () => window.removeEventListener('panel:setup-guide-open', openSetupGuide)
  }, [setupNextIncompleteIndex])

  useEffect(() => {
    if (!setupGuideOpen || !user?.firm_id || !user?.firm_store_id) return
    const loadKey = `${user.firm_id}:${user.firm_store_id}`
    if (setupCompanyLoadedKey === loadKey) return
    let cancelled = false

    async function loadSetupCompanyInfo() {
      try {
        const params = new URLSearchParams({
          firm_id: String(user?.firm_id || ''),
          store_id: String(user?.firm_store_id || ''),
        })
        const response = await fetch(`/api/setup/company-info?${params.toString()}`, {
          headers: getSetupApiHeaders(),
        })
        const data = await response.json().catch(() => null)
        if (cancelled || !response.ok || !data?.success || !data?.data?.company_info) return
        setSetupCompanyInfo((current) => ({
          ...current,
          ...data.data.company_info,
        }))
        const companyCompleted = String(data.data.settings?.setup_company_info_completed || '').toLowerCase() === 'true'
        if (companyCompleted) {
          setSetupCompletedStepIds((current) => {
            const next = new Set(current)
            next.add('company-info')
            return next
          })
          setSetupGuideStepIndex((current) => (current <= 1 ? 2 : current))
        }
        setSetupCompanyLoadedKey(loadKey)
      } catch (error) {
        console.error('[TopBar] setup company info load failed:', error)
      }
    }

    loadSetupCompanyInfo()
    return () => {
      cancelled = true
    }
  }, [setupCompanyLoadedKey, setupGuideOpen, user?.firm_id, user?.firm_store_id])

  useEffect(() => {
    if (!setupGuideOpen || !user?.firm_id || !user?.firm_store_id) return
    const loadKey = `${user.firm_id}:${user.firm_store_id}`
    if (setupVisualAssetsLoadedKey === loadKey) return
    let cancelled = false

    async function loadSetupVisualAssets() {
      try {
        const params = new URLSearchParams({
          firm_id: String(user?.firm_id || ''),
          store_id: String(user?.firm_store_id || ''),
        })
        const response = await fetch(`/api/setup/visual-assets?${params.toString()}`, {
          headers: getSetupApiHeaders(),
        })
        const data = await response.json().catch(() => null)
        if (cancelled || !response.ok || !data?.success || !data?.data?.visual_assets) return
        const visualAssets = data.data.visual_assets
        setSetupAssetPreviews((current) => ({
          ...current,
          ...(visualAssets.logoDesktop ? { logoDesktop: { name: 'Kayıtlı desktop logo', src: visualAssets.logoDesktop } } : {}),
          ...(visualAssets.logoMobile ? { logoMobile: { name: 'Kayıtlı mobil logo', src: visualAssets.logoMobile } } : {}),
          ...(visualAssets.favicon ? { favicon: { name: 'Kayıtlı favicon', src: visualAssets.favicon } } : {}),
          ...(visualAssets.lazyload ? { lazyload: { name: 'Kayıtlı LazyLoad', src: visualAssets.lazyload } } : {}),
        }))
        if (visualAssets.logoSvg) setSetupSvgLogoCode(String(visualAssets.logoSvg))
        const visualCompleted = String(data.data.settings?.setup_visual_assets_completed || '').toLowerCase() === 'true'
        if (visualCompleted) {
          setSetupCompletedStepIds((current) => {
            const next = new Set(current)
            next.add('visual-assets')
            return next
          })
          setSetupGuideStepIndex((current) => (current <= 2 ? 3 : current))
        }
        setSetupVisualAssetsLoadedKey(loadKey)
      } catch (error) {
        console.error('[TopBar] setup visual assets load failed:', error)
      }
    }

    loadSetupVisualAssets()
    return () => {
      cancelled = true
    }
  }, [setupGuideOpen, setupVisualAssetsLoadedKey, user?.firm_id, user?.firm_store_id])

  useEffect(() => {
    if (!setupGuideOpen || !user?.firm_id || !user?.firm_store_id) return
    const token = getToken()
    if (!token) return
    const loadKey = `${user.firm_id}:${user.firm_store_id}`
    if (setupPaymentsLoadedKey === loadKey) return
    let cancelled = false

    async function loadSetupPaymentLibrary() {
      setSetupPaymentLibraryLoading(true)
      setSetupPaymentLibraryError('')
      try {
        const response = await fetch(`${API_BASE}/system/payment-method-library`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        const data = await response.json().catch(() => null)
        if (!response.ok || !data?.success) {
          throw new Error(data?.message || 'Ödeme kütüphanesi alınamadı.')
        }
        if (cancelled) return
        const items = Array.isArray(data.data)
          ? data.data.map((item: Record<string, unknown>) => ({
              id: Number(item.id || 0),
              name: String(item.name || ''),
              pulled: Boolean(item.pulled),
            })).filter((item: SetupPaymentLibraryItem) => item.id > 0 && item.name)
          : []
        setSetupPaymentLibrary(items)
        setSetupSelectedPaymentIds(items.filter((item: SetupPaymentLibraryItem) => item.pulled).map((item: SetupPaymentLibraryItem) => item.id))
        if (items.some((item: SetupPaymentLibraryItem) => item.pulled)) {
          setSetupCompletedStepIds((current) => {
            const next = new Set(current)
            next.add('payments')
            return next
          })
        }
        setSetupPaymentsLoadedKey(loadKey)
      } catch (error) {
        if (!cancelled) setSetupPaymentLibraryError(error instanceof Error ? error.message : 'Ödeme kütüphanesi alınamadı.')
      } finally {
        if (!cancelled) setSetupPaymentLibraryLoading(false)
      }
    }

    loadSetupPaymentLibrary()
    return () => {
      cancelled = true
    }
  }, [setupGuideOpen, setupPaymentsLoadedKey, user?.firm_id, user?.firm_store_id])

  useEffect(() => {
    if (typeof document === 'undefined') return
    document.body.classList.toggle('store-settings-pinned-open', storeSettingsOpen && storeSettingsPinned)
    document.body.classList.toggle('setup-tasks-pinned-open', setupTasksOpen)
    return () => {
      document.body.classList.remove('store-settings-pinned-open')
      document.body.classList.remove('setup-tasks-pinned-open')
    }
  }, [setupTasksOpen, storeSettingsOpen, storeSettingsPinned])

  const handleStoreSettingsToggle = (checked: boolean) => {
    setStoreSettingsOpen(checked)
    if (checked) loadStoreSettings()
  }

  const handleSetupStepTooltip = (
    event: ReactMouseEvent<HTMLButtonElement> | FocusEvent<HTMLButtonElement>,
    text: string
  ) => {
    const rect = event.currentTarget.getBoundingClientRect()
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0
    const edgeSpace = 180
    const placement = rect.left < edgeSpace
      ? 'left'
      : viewportWidth - rect.right < edgeSpace
        ? 'right'
        : 'center'
    setSetupStepTooltip({
      text,
      left: placement === 'left'
        ? Math.max(12, rect.left)
        : placement === 'right'
          ? Math.min(viewportWidth - 12, rect.right)
          : rect.left + rect.width / 2,
      top: rect.bottom + 7,
      placement,
    })
  }

  const handleSetupCompanyInfoChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.currentTarget
    const field = name as keyof SetupCompanyInfoForm
    setSetupCompanyInfo((current) => ({
      ...current,
      [field]: value,
    }))
  }

  const isSetupCompanyFieldInvalid = (field: keyof SetupCompanyInfoForm) => (
    setupCompanySubmitted
    && SETUP_COMPANY_REQUIRED_FIELDS.includes(field)
    && !String(setupCompanyInfo[field] || '').trim()
  )

  const setupCompanyFieldClass = (field: keyof SetupCompanyInfoForm, extra = '') => {
    const classes = ['setup-guide__field']
    if (extra) classes.push(extra)
    if (isSetupCompanyFieldInvalid(field)) classes.push('is-invalid')
    return classes.join(' ')
  }

  const validateSetupCompanyInfo = () => (
    SETUP_COMPANY_REQUIRED_FIELDS.every((field) => String(setupCompanyInfo[field] || '').trim())
  )

  const saveSetupCompanyInfo = async () => {
    if (!user?.firm_id || !user?.firm_store_id) {
      throw new Error('Firma veya mağaza bilgisi bulunamadı.')
    }

    const response = await fetch('/api/setup/company-info', {
      method: 'POST',
      headers: getSetupApiHeaders(true),
      body: JSON.stringify({
        firm_id: user.firm_id,
        store_id: user.firm_store_id,
        company_info: setupCompanyInfo,
      }),
    })
    const data = await response.json().catch(() => null)
    if (!response.ok || !data?.success) {
      throw new Error(data?.message || 'Firma bilgileri kaydedilemedi.')
    }
    window.dispatchEvent(new CustomEvent('setup-tasks:changed'))
  }

  const getSetupVisualAssetsPayload = () => ({
    logoDesktop: setupAssetPreviews.logoDesktop?.src || '',
    logoMobile: setupAssetPreviews.logoMobile?.src || '',
    logoSvg: setupSvgLogoCode.trim(),
    favicon: setupAssetPreviews.favicon?.src || '',
    lazyload: setupAssetPreviews.lazyload?.src || '',
  })

  const saveSetupVisualAssets = async (options?: { skipped?: boolean }) => {
    if (!user?.firm_id || !user?.firm_store_id) {
      throw new Error('Firma veya mağaza bilgisi bulunamadı.')
    }

    const response = await fetch('/api/setup/visual-assets', {
      method: 'POST',
      headers: getSetupApiHeaders(true),
      body: JSON.stringify({
        firm_id: user.firm_id,
        store_id: user.firm_store_id,
        skipped: Boolean(options?.skipped),
        visual_assets: getSetupVisualAssetsPayload(),
      }),
    })
    const data = await response.json().catch(() => null)
    if (!response.ok || !data?.success) {
      throw new Error(data?.message || 'Görsel bilgiler kaydedilemedi.')
    }
    window.dispatchEvent(new CustomEvent('setup-tasks:changed'))
  }

  const toggleSetupPaymentLibraryItem = (paymentId: number) => {
    setSetupSelectedPaymentIds((current) => (
      current.includes(paymentId)
        ? current.filter((id) => id !== paymentId)
        : [...current, paymentId]
    ))
  }

  const saveSetupPayments = async () => {
    if (!user?.firm_id || !user?.firm_store_id) {
      throw new Error('Firma veya mağaza bilgisi bulunamadı.')
    }
    const token = getToken()
    if (!token) {
      throw new Error('Oturum bilgisi bulunamadı.')
    }
    const selectedIds = [...new Set(setupSelectedPaymentIds.map((id) => Number(id)).filter(Boolean))]
    if (!selectedIds.length) {
      throw new Error('En az bir ödeme yöntemi seçin.')
    }

    const importResponse = await fetch(`${API_BASE}/system/payment-methods/import`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ ids: selectedIds }),
    })
    const importData = await importResponse.json().catch(() => null)
    if (!importResponse.ok || !importData?.success) {
      throw new Error(importData?.message || 'Ödeme yöntemleri firmaya aktarılamadı.')
    }

    const completeResponse = await fetch('/api/setup/payments', {
      method: 'POST',
      headers: getSetupApiHeaders(true),
      body: JSON.stringify({
        firm_id: user.firm_id,
        store_id: user.firm_store_id,
      }),
    })
    const completeData = await completeResponse.json().catch(() => null)
    if (!completeResponse.ok || !completeData?.success) {
      throw new Error(completeData?.message || 'Ödeme adımı tamamlandı olarak işaretlenemedi.')
    }

    setSetupPaymentLibrary((current) => current.map((item) => ({
      ...item,
      pulled: selectedIds.includes(item.id) || item.pulled,
    })))
  }

  const markSetupStepCompleted = (stepId: string) => {
    setSetupCompletedStepIds((current) => {
      const next = new Set(current)
      next.add(stepId)
      return next
    })
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('panel:setup-status-changed'))
    }
  }

  const goToSetupGuideStep = async (targetIndex: number) => {
    if (activeSetupStep.id === 'company-info' && targetIndex > setupGuideStepIndex) {
      setSetupCompanySubmitted(true)
      if (!validateSetupCompanyInfo()) {
        showToast('Lütfen yıldızlı zorunlu firma bilgilerini tamamlayın.', 'error')
        return
      }
      setSetupCompanySaving(true)
      try {
        await saveSetupCompanyInfo()
        markSetupStepCompleted('company-info')
        showToast('Firma bilgileri kaydedildi.', 'success')
      } catch (error) {
        showToast(error instanceof Error ? error.message : 'Firma bilgileri kaydedilemedi.', 'error')
        return
      } finally {
        setSetupCompanySaving(false)
      }
    }
    if (activeSetupStep.id === 'visual-assets' && targetIndex > setupGuideStepIndex) {
      if (!setupAssetPreviews.logoDesktop?.src) {
        showToast('Lütfen düz/desktop logoyu yükleyin veya bu adımı daha sonra ayarlayın.', 'error')
        return
      }
      setSetupVisualAssetsSaving(true)
      try {
        await saveSetupVisualAssets()
        markSetupStepCompleted('visual-assets')
        showToast('Logo ve görsel bilgileri kaydedildi.', 'success')
      } catch (error) {
        showToast(error instanceof Error ? error.message : 'Görsel bilgiler kaydedilemedi.', 'error')
        return
      } finally {
        setSetupVisualAssetsSaving(false)
      }
    }
    if (activeSetupStep.id === 'payments' && targetIndex > setupGuideStepIndex) {
      setSetupPaymentsSaving(true)
      try {
        await saveSetupPayments()
        markSetupStepCompleted('payments')
        showToast('Ödeme yöntemleri firmaya aktarıldı.', 'success')
      } catch (error) {
        showToast(error instanceof Error ? error.message : 'Ödeme yöntemleri kaydedilemedi.', 'error')
        return
      } finally {
        setSetupPaymentsSaving(false)
      }
    }
    const nextIndex = Math.max(0, Math.min(SETUP_GUIDE_STEPS.length - 1, targetIndex))
    setSetupGuideStepIndex(nextIndex)
  }

  const skipSetupVisualAssetsStep = async () => {
    setSetupVisualAssetsSaving(true)
    try {
      await saveSetupVisualAssets({ skipped: true })
      markSetupStepCompleted('visual-assets')
      setSetupGuideStepIndex(Math.min(SETUP_GUIDE_STEPS.length - 1, setupGuideStepIndex + 1))
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Görsel adımı atlanamadı.', 'error')
    } finally {
      setSetupVisualAssetsSaving(false)
    }
  }

  const handleSetupAssetChange = (key: SetupAssetKey, file?: File | null) => {
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => {
      setSetupAssetPreviews((current) => ({
        ...current,
        [key]: {
          name: file.name,
          src: String(reader.result || ''),
        },
      }))
    }
    reader.readAsDataURL(file)
  }

  const handleStoreSettingsClose = () => {
    if (storeSettingsPinned) {
      setStoreSettingsPinned(false)
      localStorage.setItem(STORE_SETTINGS_PINNED_KEY, '0')
    }
    setStoreSettingsOpen(false)
  }

  const handleStoreSettingsPin = () => {
    setStoreSettingsPinned((pinned) => {
      const next = !pinned
      localStorage.setItem(STORE_SETTINGS_PINNED_KEY, next ? '1' : '0')
      return next
    })
    setStoreSettingsOpen(true)
  }

  const handleSetupTasksOpen = () => {
    setSetupTasksOpen(true)
    loadSetupTasks()
  }

  const updateSetupTaskStatus = async (task: SetupTaskItem, status: SetupTaskStatus) => {
    if (!user?.firm_id || !user?.firm_store_id) return
    const token = getToken()
    if (!token) return
    setSetupTasksLoading(true)
    setSetupTasksError('')
    try {
      const response = await fetch('/api/setup/tasks', {
        method: 'PATCH',
        headers: getSetupApiHeaders(true),
        body: JSON.stringify({
          firm_id: user.firm_id,
          store_id: user.firm_store_id,
          task_id: task.id,
          status,
        }),
      })
      const data = await response.json().catch(() => null)
      if (!response.ok || !data?.success) throw new Error(data?.message || 'Görev güncellenemedi.')
      setSetupTasks(Array.isArray(data.data?.tasks) ? data.data.tasks : [])
      setSetupTaskSummary(data.data?.summary && typeof data.data.summary === 'object' ? data.data.summary : {})
    } catch (error) {
      setSetupTasksError(error instanceof Error ? error.message : 'Görev güncellenemedi.')
    } finally {
      setSetupTasksLoading(false)
    }
  }

  const openSetupTaskTarget = (task: SetupTaskItem) => {
    const targetUrl = String(task.target_url || '').trim()
    if (!targetUrl) return
    if (/^https?:\/\//i.test(targetUrl)) {
      window.location.href = targetUrl
      return
    }
    router.push(targetUrl)
  }

  const handleStoreSettingsReset = async () => {
    const token = getToken()
    if (!token || !isAdmin) return
    setStoreSettingsResetting(true)
    setStoreSettingsError('')
    try {
      const response = await fetch(`${API_BASE}/panel/store-settings-cache/reset`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ storeId: user?.firm_store_id || storeSettings?.storeId || 0 }),
      })
      const data = await response.json().catch(() => null)
      if (!response.ok || !data?.success) throw new Error(data?.message || 'Cache resetlenemedi')
      await loadStoreSettings()
    } catch (error) {
      setStoreSettingsError(error instanceof Error ? error.message : 'Cache resetlenemedi')
    } finally {
      setStoreSettingsResetting(false)
    }
  }

  const handleGlobalCacheClear = useCallback(async () => {
    const token = getToken()
    if (!token) {
      showToast('Oturum bulunamadı', 'error')
      return
    }
    setCacheClearing(true)
    try {
      const response = await fetch(`${API_BASE}/panel/cache/clear`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })
      const data = await response.json().catch(() => null)
      if (!response.ok || !data?.success) {
        throw new Error(data?.message || 'Cache silinemedi')
      }
      showToast(data?.message || 'Genel cache temizlendi', 'success')
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Cache silinemedi', 'error')
    } finally {
      setCacheClearing(false)
    }
  }, [showToast])

  return (
    <header className="panel-topbar">
      {/* Left Side */}
      <div className="panel-topbar__left">
        {/* Hamburger — mobile only, shown via CSS */}
        <button
          className="panel-topbar__hamburger"
          onClick={() => setMobileSidebarOpen(!mobileSidebarOpen)}
          aria-label="Menüyü aç/kapat"
        >
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
        <div className="panel-topbar__store-selector">
          {storeSelector}
        </div>
        {isAdmin && (
          <button
            type="button"
            className="panel-topbar__setup-start"
            onClick={() => setSetupGuideOpen(true)}
          >
            <i className="fa-regular fa-circle-play" />
            <span>Kuruluma Başla</span>
          </button>
        )}
      </div>

      {/* Right Side */}
      <div className="panel-topbar__right">
        {isAdmin && (
          <>
            <button
              type="button"
              className={`panel-topbar__setup-tasks${openSetupTaskCount > 0 ? ' has-open-tasks' : ''}`}
              onClick={handleSetupTasksOpen}
              aria-label={setupTaskButtonLabel}
            >
              {setupTasksLoading && setupTasks.length === 0 && <i className="fa-regular fa-spinner-third fa-spin" />}
              <span>{setupTaskButtonLabel}</span>
            </button>

            {/* Search */}
            <button
              type="button"
              className="panel-topbar__search"
              onClick={() => setCommandSearchOpen(true)}
              aria-label="Panelde ara"
              aria-expanded={commandSearchOpen}
              data-onboarding-target="topbar-search"
            >
              <svg className="panel-topbar__search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <circle cx="11" cy="11" r="8" strokeWidth={2} />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35" />
              </svg>
              <span className="panel-topbar__search-text">Sipariş, ürün, yardım ara</span>
              <kbd className="panel-topbar__search-kbd">⌘ K</kbd>
            </button>

            <button
              type="button"
              className="panel-topbar__btn"
              onClick={handleGlobalCacheClear}
              disabled={cacheClearing}
            >
              {cacheClearing ? 'Siliniyor...' : 'Cache Sil'}
            </button>
          </>
        )}

        {isAdmin && (
          <label className="panel-topbar__settings-toggle" title="Mağaza ayarları JSON">
            <input
              type="checkbox"
              checked={storeSettingsOpen}
              onChange={(event) => handleStoreSettingsToggle(event.target.checked)}
            />
            <span className="panel-topbar__settings-toggle-track">
              <span className="panel-topbar__settings-toggle-knob" />
            </span>
            <span className="panel-topbar__settings-toggle-text">Mağaza Ayarları</span>
          </label>
        )}

        {/* Notification */}
        <div className="panel-topbar__notification" ref={notificationRef}>
          <button
            className="panel-topbar__icon-btn panel-topbar__icon-btn--notification"
            data-icon-tooltip={notificationOpen ? undefined : 'Bildirimler'}
            onClick={handleNotificationToggle}
            aria-label="Bildirimler"
            aria-expanded={notificationOpen}
          >
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            {unreadCount > 0 && (
              <span className="panel-topbar__notification-badge">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>
          {notificationOpen && (
            <div className="panel-notification-menu">
              <div className="panel-notification-menu__head">
                <div>
                  <strong>Bildirimler</strong>
                  <span>{unreadCount > 0 ? `${unreadCount} okunmamış` : 'Yeni bildirim yok'}</span>
                </div>
                <button type="button" onClick={handleReadAll} disabled={unreadCount === 0}>
                  Okundu yap
                </button>
              </div>
              <div className="panel-notification-menu__list">
                {notificationLoading && <div className="panel-notification-menu__empty">Bildirimler yükleniyor...</div>}
                {!notificationLoading && notifications.length === 0 && (
                  <div className="panel-notification-menu__empty">Henüz bildirimin yok.</div>
                )}
                {!notificationLoading && notifications.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className={item.read_at ? 'panel-notification-item' : 'panel-notification-item is-unread'}
                    onClick={() => handleNotificationClick(item)}
                  >
                    <span className="panel-notification-item__icon">
                      <i className={item.type === 'order_note_added' ? 'fa-regular fa-message-lines' : 'fa-regular fa-bell'} />
                    </span>
                    <span className="panel-notification-item__body">
                      <strong>{item.title || 'Bildirim'}</strong>
                      <span>{item.body || '-'}</span>
                      <em>{formatNotificationTime(item.created_at)}</em>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Settings */}
        <button 
          className="panel-topbar__icon-btn"
          data-icon-tooltip="Ayarlar"
        >
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
          </svg>
        </button>

        {/* Help */}
        <button 
          className="panel-topbar__icon-btn"
          data-icon-tooltip="Yardım"
        >
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </button>

        {/* User Dropdown */}
        <UserDropdown user={currentUser} />
      </div>

      {isAdmin && commandSearchOpen && typeof document !== 'undefined' && createPortal((
        <div className="command-search" role="dialog" aria-modal="true" aria-label="Panel araması">
          <button
            type="button"
            className="command-search__backdrop"
            aria-label="Aramayı kapat"
            onClick={() => setCommandSearchOpen(false)}
          />
          <div className="command-search__panel" data-onboarding-target="search-modal">
            <div className="command-search__head">
              <i className="fa-regular fa-magnifying-glass" />
              <input
                ref={commandSearchInputRef}
                value={commandSearchQuery}
                onChange={(event) => setCommandSearchQuery(event.target.value)}
                placeholder="Sipariş, ürün, müşteri, fatura, yardım, menü veya ayar ara"
                aria-label="Panel araması"
              />
              {commandSearchQuery && (
                <button type="button" className="command-search__clear" onClick={() => setCommandSearchQuery('')}>
                  Temizle
                </button>
              )}
              <button
                type="button"
                className="command-search__close"
                onClick={() => setCommandSearchOpen(false)}
                aria-label="Aramayı kapat"
              >
                <i className="fa-regular fa-xmark" />
              </button>
            </div>

            <div className="command-search__body">
              {commandSearchHelpLoading && commandSearchQuery.trim().length >= 2 && (
                <div className="command-search__loading">
                  <i className="fa-regular fa-spinner-third" />
                  Yardım ve modül içerikleri taranıyor
                </div>
              )}
              {filteredCommandSearchItems.length === 0 ? (
                <div className="command-search__empty">
                  <i className="fa-regular fa-compass" />
                  <strong>Sonuç bulunamadı</strong>
                  <span>Farklı bir ürün kodu, müşteri adı, sipariş no, menü adı veya “nasıl” sorusu deneyin.</span>
                </div>
              ) : (
                Object.entries(
                  filteredCommandSearchItems.reduce<Record<string, CommandSearchItem[]>>((groups, item) => {
                    groups[item.group] = groups[item.group] || []
                    groups[item.group].push(item)
                    return groups
                  }, {})
                ).map(([group, items]) => (
                  <section key={group} className="command-search__section">
                    <h3>{group}</h3>
                    <div className="command-search__list">
                      {items.map((item) => {
                        const globalIndex = filteredCommandSearchItems.findIndex((candidate) => candidate.id === item.id)
                        return (
                          <button
                            key={item.id}
                            type="button"
                            className={`command-search__item ${globalIndex === commandSearchActiveIndex ? 'is-active' : ''}`}
                            onMouseEnter={() => setCommandSearchActiveIndex(globalIndex)}
                            onClick={() => handleCommandSearchSelect(item)}
                          >
                            <span className="command-search__item-icon">
                              <i className={item.icon} />
                            </span>
                            <span className="command-search__item-copy">
                              <strong>{renderCommandSearchHighlightedText(item.title, commandSearchQuery)}</strong>
                              {item.type === 'ai-answer' ? (
                                <span className="command-search__rich-answer">
                                  {renderCommandSearchRichText(item.answer || item.subtitle, commandSearchQuery)}
                                </span>
                              ) : (
                                <em>{renderCommandSearchHighlightedText(item.answer || item.subtitle, commandSearchQuery)}</em>
                              )}
                              {(item.sourceTitle || item.sourceType) && (
                                <small>{renderCommandSearchHighlightedText([item.sourceType, item.sourceTitle].filter(Boolean).join(' · '), commandSearchQuery)}</small>
                              )}
                            </span>
                            <span className="command-search__enter">
                              <i className="fa-regular fa-turn-down-left" />
                            </span>
                          </button>
                        )
                      })}
                    </div>
                  </section>
                ))
              )}
            </div>

            <div className="command-search__footer">
              <span><kbd>↑</kbd><kbd>↓</kbd> Gezin</span>
              <span><kbd>Enter</kbd> Aç</span>
              <span><kbd>Esc</kbd> Kapat</span>
              <button type="button" onClick={() => showToast('Global arama önerilerini birlikte netleştiriyoruz.', 'info')}>
                <i className="fa-regular fa-message-question" />
                Geri bildirim
              </button>
            </div>
          </div>
        </div>
      ), document.body)}

      {setupGuideOpen && typeof document !== 'undefined' && createPortal(
        <div className="setup-guide" role="dialog" aria-modal="true" aria-labelledby="setup-guide-title">
          <div className="setup-guide__top">
            <div className="setup-guide__brand">
              <span className="setup-guide__brand-mark">
                <i className="fa-regular fa-wand-magic-sparkles" />
              </span>
              <div>
                <strong id="setup-guide-title">Mağaza Kurulum Rehberi</strong>
                <span>Adım adım ilerleyin, eksik kalan yerleri sonra tamamlayın.</span>
              </div>
            </div>
            <div className="setup-guide__top-actions">
              <span className="setup-guide__draft">Kurulum rehberi</span>
              <button type="button" className="setup-guide__close" onClick={() => setSetupGuideOpen(false)} aria-label="Kurulum rehberini kapat">
                <i className="fa-regular fa-xmark" />
              </button>
            </div>
          </div>

          <div className="setup-guide__progress">
            <div>
              <strong>{setupGuideStepIndex === 0 ? 'Başlangıç' : `${setupGuideStepIndex}. adım`}</strong>
              <span>{setupGuideStepIndex === 0 ? `Başlangıç ve ${SETUP_GUIDE_STEPS.length - 1} kurulum adımı` : `${SETUP_GUIDE_STEPS.length - 1} adımdan ${setupGuideStepIndex}. adım`}</span>
            </div>
            <div className="setup-guide__progress-track" aria-label={`Kurulum ilerleme: ${setupProgress}%`}>
              <span style={{ width: `${setupProgress}%` }} />
            </div>
            <em>{setupProgress}%</em>
          </div>

          <nav className="setup-guide__steps" aria-label="Kurulum adımları">
            <div className="setup-guide__step-list">
              {SETUP_GUIDE_STEPS.map((step, index) => (
                <button
                  key={step.id}
                  type="button"
                  className={`setup-guide__step ${index === setupGuideStepIndex ? 'is-active' : ''} ${index < setupGuideStepIndex || setupCompletedStepIds.has(step.id) ? 'is-done' : ''}`}
                  onClick={() => goToSetupGuideStep(index)}
                  disabled={setupCompanySaving || setupVisualAssetsSaving || setupPaymentsSaving}
                  onMouseEnter={(event) => handleSetupStepTooltip(event, `${index === 0 ? 'Başlangıç' : `${index}. adım`} · ${step.title}`)}
                  onMouseLeave={() => setSetupStepTooltip(null)}
                  onFocus={(event) => handleSetupStepTooltip(event, `${index === 0 ? 'Başlangıç' : `${index}. adım`} · ${step.title}`)}
                  onBlur={() => setSetupStepTooltip(null)}
                  aria-label={`${index === 0 ? 'Başlangıç' : `${index}. adım`}: ${step.title}`}
                >
                  <span>{index < setupGuideStepIndex || setupCompletedStepIds.has(step.id) ? <i className="fa-regular fa-check" /> : index === 0 ? 'B' : index}</span>
                </button>
              ))}
            </div>
          </nav>

          {setupStepTooltip && (
            <div
              className={`setup-guide__step-tooltip setup-guide__step-tooltip--${setupStepTooltip.placement}`}
              style={{ left: setupStepTooltip.left, top: setupStepTooltip.top }}
            >
              {setupStepTooltip.text}
            </div>
          )}

          <div className="setup-guide__body">
            <main className="setup-guide__content">
              <div className="setup-guide__content-head">
                <span>{activeSetupStep.eyebrow}</span>
                <h2>{activeSetupStep.title}</h2>
                <p>{activeSetupStep.summary}</p>
              </div>

              {activeSetupStep.id === 'company-info' && (
                <section className="setup-guide__section setup-guide__form-section">
                  <div className="setup-guide__form-head">
                    <div>
                      <h3>Firma bilgileri</h3>
                      <p>Bu bilgiler web sitesi kurulum ayarlarına kaydedilir.</p>
                    </div>
                  </div>

                  <div className="setup-guide__form">
                    <fieldset className="setup-guide__fieldset">
                      <legend>Resmi firma bilgileri</legend>
                      <div className="setup-guide__field-grid">
                        <label className={setupCompanyFieldClass('legalName', 'setup-guide__field--wide')}>
                          <span>Resmi firma adı <em>*</em></span>
                          <input
                            name="legalName"
                            value={setupCompanyInfo.legalName}
                            onChange={handleSetupCompanyInfoChange}
                            placeholder="Örn. Farktor Moda Tekstil San. ve Tic. A.Ş."
                          />
                        </label>
                        <label className={setupCompanyFieldClass('tradeName')}>
                          <span>Mağaza / marka adı <em>*</em></span>
                          <input
                            name="tradeName"
                            value={setupCompanyInfo.tradeName}
                            onChange={handleSetupCompanyInfoChange}
                            placeholder="Örn. Farktor Moda"
                          />
                        </label>
                        <label className={setupCompanyFieldClass('taxOffice')}>
                          <span>Vergi dairesi <em>*</em></span>
                          <input
                            name="taxOffice"
                            value={setupCompanyInfo.taxOffice}
                            onChange={handleSetupCompanyInfoChange}
                            placeholder="Örn. Kadıköy"
                          />
                        </label>
                        <label className={setupCompanyFieldClass('taxNumber')}>
                          <span>Vergi numarası <em>*</em></span>
                          <input
                            name="taxNumber"
                            value={setupCompanyInfo.taxNumber}
                            onChange={handleSetupCompanyInfoChange}
                            placeholder="10 haneli vergi numarası"
                          />
                        </label>
                      </div>
                    </fieldset>

                    <fieldset className="setup-guide__fieldset">
                      <legend>Adres ve firma iletişimi</legend>
                      <div className="setup-guide__field-grid">
                        <label className={setupCompanyFieldClass('address', 'setup-guide__field--wide')}>
                          <span>Firma adresi <em>*</em></span>
                          <textarea
                            name="address"
                            value={setupCompanyInfo.address}
                            onChange={handleSetupCompanyInfoChange}
                            placeholder="Mahalle, cadde/sokak, bina no, kat/daire bilgileri"
                            rows={3}
                          />
                        </label>
                        <label className={setupCompanyFieldClass('city')}>
                          <span>İl <em>*</em></span>
                          <input
                            name="city"
                            value={setupCompanyInfo.city}
                            onChange={handleSetupCompanyInfoChange}
                            placeholder="Örn. İstanbul"
                          />
                        </label>
                        <label className={setupCompanyFieldClass('district')}>
                          <span>İlçe <em>*</em></span>
                          <input
                            name="district"
                            value={setupCompanyInfo.district}
                            onChange={handleSetupCompanyInfoChange}
                            placeholder="Örn. Kadıköy"
                          />
                        </label>
                        <label className={setupCompanyFieldClass('companyPhone')}>
                          <span>Firma telefonu <em>*</em></span>
                          <input
                            name="companyPhone"
                            value={setupCompanyInfo.companyPhone}
                            onChange={handleSetupCompanyInfoChange}
                            placeholder="0212 ___ __ __"
                          />
                        </label>
                        <label className={setupCompanyFieldClass('companyEmail')}>
                          <span>Firma e-posta adresi <em>*</em></span>
                          <input
                            name="companyEmail"
                            type="email"
                            value={setupCompanyInfo.companyEmail}
                            onChange={handleSetupCompanyInfoChange}
                            placeholder="info@firma.com"
                          />
                        </label>
                      </div>
                    </fieldset>
                  </div>
                </section>
              )}

              {activeSetupStep.id === 'visual-assets' && (
                <section className="setup-guide__section setup-guide__asset-section">
                  <div className="setup-guide__form-head">
                    <div>
                      <h3>Görsel dosyalar</h3>
                      <p>Düz logo zorunludur; yüklenen görseller web sitesi kurulum ayarlarına kaydedilir.</p>
                    </div>
                  </div>

                  <div className="setup-guide__asset-group">
                    <div className="setup-guide__asset-group-head">
                      <h4>Logolar</h4>
                      <span>SVG formatı önerilir</span>
                    </div>
                    <div className="setup-guide__asset-grid setup-guide__asset-grid--logos">
                      {SETUP_LOGO_ASSETS.map((asset) => {
                        const preview = setupAssetPreviews[asset.key]
                        const isSvgCode = asset.key === 'logoSvg'
                        return (
                          <label key={asset.key} className={`setup-guide__asset-card${preview || (isSvgCode && setupSvgLogoCode.trim()) ? ' is-ready' : ''}`}>
                            {!isSvgCode && (
                              <input
                                type="file"
                                accept={asset.accept}
                                onChange={(event) => handleSetupAssetChange(asset.key, event.currentTarget.files?.[0])}
                              />
                            )}
                            <span className="setup-guide__asset-card-head">
                              <i className={preview || (isSvgCode && setupSvgLogoCode.trim()) ? 'fa-regular fa-circle-check' : 'fa-regular fa-cloud-arrow-up'} />
                              <strong>{asset.title}</strong>
                            </span>
                            <span className="setup-guide__asset-recommend">SVG formatı önerilir</span>
                            <p>{asset.description}</p>
                            <ul>
                              <li><b>Format:</b> {asset.formats}</li>
                              <li><b>Boyut:</b> {asset.size}</li>
                              <li><b>Dosya:</b> {asset.maxSize}</li>
                            </ul>
                            {isSvgCode ? (
                              <textarea
                                className="setup-guide__asset-code"
                                value={setupSvgLogoCode}
                                onChange={(event) => setSetupSvgLogoCode(event.currentTarget.value)}
                                placeholder="<svg ...>...</svg>"
                                rows={5}
                              />
                            ) : (
                              <span className="setup-guide__asset-preview">
                                {preview ? (
                                  <>
                                    <span
                                      className="setup-guide__asset-preview-image"
                                      style={{ backgroundImage: `url(${preview.src})` }}
                                      aria-label={`${asset.title} önizleme`}
                                      role="img"
                                    />
                                    <small>{preview.name}</small>
                                  </>
                                ) : (
                                  <small>Dosya seç</small>
                                )}
                              </span>
                            )}
                          </label>
                        )
                      })}
                    </div>
                  </div>

                  <div className="setup-guide__asset-group">
                    <div className="setup-guide__asset-group-head">
                      <h4>Favicon ve LazyLoad</h4>
                    </div>
                    <div className="setup-guide__asset-grid setup-guide__asset-grid--support">
                      {SETUP_OTHER_ASSETS.map((asset) => {
                        const preview = setupAssetPreviews[asset.key]
                        return (
                          <label key={asset.key} className={`setup-guide__asset-card${preview ? ' is-ready' : ''}`}>
                            <input
                              type="file"
                              accept={asset.accept}
                              onChange={(event) => handleSetupAssetChange(asset.key, event.currentTarget.files?.[0])}
                            />
                            <span className="setup-guide__asset-card-head">
                              <i className={preview ? 'fa-regular fa-circle-check' : 'fa-regular fa-cloud-arrow-up'} />
                              <strong>{asset.title}</strong>
                            </span>
                            <p>{asset.description}</p>
                            <ul>
                              <li><b>Format:</b> {asset.formats}</li>
                              <li><b>Boyut:</b> {asset.size}</li>
                              <li><b>Dosya:</b> {asset.maxSize}</li>
                            </ul>
                            <span className="setup-guide__asset-preview">
                              {preview ? (
                                <>
                                  <span
                                    className="setup-guide__asset-preview-image"
                                    style={{ backgroundImage: `url(${preview.src})` }}
                                    aria-label={`${asset.title} önizleme`}
                                    role="img"
                                  />
                                  <small>{preview.name}</small>
                                </>
                              ) : (
                                <small>Dosya seç</small>
                              )}
                            </span>
                          </label>
                        )
                      })}
                    </div>
                  </div>
                </section>
              )}

              {activeSetupStep.id === 'payments' && (
                <section className="setup-guide__section setup-guide__payment-section">
                  <div className="setup-guide__form-head">
                    <div>
                      <h3>Ödeme seçenekleri</h3>
                      <p>farktor_main ödeme kütüphanesinden kullanılacak yöntemleri seçin; sonraki adımda seçilenler firmanıza aktarılır.</p>
                    </div>
                  </div>

                  <div className="setup-guide__payment-library">
                    {setupPaymentLibraryLoading && (
                      <div className="setup-guide__empty-state">
                        <i className="fa-regular fa-spinner-third fa-spin" />
                        <span>Ödeme kütüphanesi yükleniyor.</span>
                      </div>
                    )}
                    {!setupPaymentLibraryLoading && setupPaymentLibraryError && (
                      <div className="setup-guide__empty-state setup-guide__empty-state--danger">
                        <i className="fa-regular fa-triangle-exclamation" />
                        <span>{setupPaymentLibraryError}</span>
                      </div>
                    )}
                    {!setupPaymentLibraryLoading && !setupPaymentLibraryError && setupPaymentLibrary.length === 0 && (
                      <div className="setup-guide__empty-state">
                        <i className="fa-regular fa-credit-card" />
                        <span>Ödeme kütüphanesinde listelenecek seçenek bulunamadı.</span>
                      </div>
                    )}
                    {!setupPaymentLibraryLoading && !setupPaymentLibraryError && setupPaymentLibrary.length > 0 && (
                      <div className="setup-guide__payment-methods">
                        {setupPaymentLibrary.map((method) => {
                          const selected = setupSelectedPaymentIds.includes(method.id)
                          return (
                            <label
                              key={method.id}
                              className={`setup-guide__payment-method${selected ? ' is-selected' : ''}${method.pulled ? ' is-ready' : ''}`}
                            >
                              <input
                                type="checkbox"
                                checked={selected}
                                onChange={() => toggleSetupPaymentLibraryItem(method.id)}
                              />
                              <span>
                                <i className="fa-regular fa-credit-card" />
                              </span>
                              <strong>{method.name}</strong>
                              <small>{method.pulled ? 'Firmaya ekli' : 'Kütüphaneden firmaya aktarılacak'}</small>
                            </label>
                          )
                        })}
                      </div>
                    )}
                  </div>
                </section>
              )}

              {activeSetupStep.id === 'cargo' && (
                <section className="setup-guide__section setup-guide__cargo-section">
                  <div className="setup-guide__form-head">
                    <div>
                      <h3>Kargo firmaları ve teslimat baremleri</h3>
                      <p>Bu adımda kargo entegrasyonları hazırlanır; alanlar şimdilik kayıt edilmez ve zorunlu kontrol uygulanmaz.</p>
                    </div>
                  </div>

                  <div className="setup-guide__cargo-provider-grid">
                    {SETUP_CARGO_PROVIDERS.map((provider, index) => (
                      <label key={provider.id} className={`setup-guide__cargo-provider${index === 0 ? ' is-selected' : ''}`}>
                        <input type="checkbox" defaultChecked={index === 0} />
                        <span className="setup-guide__cargo-provider-icon">
                          <i className="fa-regular fa-truck-fast" />
                        </span>
                        <strong>{provider.title}</strong>
                        <small>{provider.description}</small>
                        <em>{provider.fields.length} bilgi alanı</em>
                      </label>
                    ))}
                  </div>

                  <div className="setup-guide__cargo-layout">
                    <fieldset className="setup-guide__fieldset setup-guide__cargo-panel">
                      <legend>Seçili kargo entegrasyon bilgileri</legend>
                      <div className="setup-guide__cargo-required-fields">
                        {SETUP_CARGO_PROVIDERS[0].fields.map((field) => (
                          <label key={field} className="setup-guide__field">
                            <span>{field} <em>*</em></span>
                            <input placeholder={`${field} bilgisini girin`} />
                          </label>
                        ))}
                        <label className="setup-guide__field setup-guide__field--wide">
                          <span>Entegrasyon notu</span>
                          <textarea rows={3} placeholder="Kargo firmasına özel şube, cari, kapıda ödeme veya anlaşma notları" />
                        </label>
                      </div>

                      <button type="button" className="setup-guide__ghost-action">
                        <i className="fa-regular fa-signal-stream" />
                        Bağlantıyı test et
                      </button>
                    </fieldset>

                    <fieldset className="setup-guide__fieldset setup-guide__cargo-panel">
                      <legend>Kargo baremleri</legend>
                      <div className="setup-guide__cargo-band-list">
                        {SETUP_CARGO_BANDS.map((band) => (
                          <label key={band.label} className="setup-guide__cargo-band">
                            <span>{band.label}</span>
                            <input defaultValue={band.price} aria-label={`${band.label} kargo ücreti`} />
                            <small>TL + KDV</small>
                          </label>
                        ))}
                      </div>
                      <label className="setup-guide__field">
                        <span>Ücretsiz kargo limiti</span>
                        <input placeholder="Örn. 1500 TL" />
                      </label>
                    </fieldset>
                  </div>

                  <div className="setup-guide__cargo-extra-grid">
                    <fieldset className="setup-guide__fieldset">
                      <legend>Kapıda ödeme baremleri</legend>
                      <div className="setup-guide__field-grid">
                        <label className="setup-guide__field">
                          <span>Nakit tahsilat ücreti <em>*</em></span>
                          <input placeholder="Örn. 29,90 TL" />
                        </label>
                        <label className="setup-guide__field">
                          <span>POS tahsilat ücreti <em>*</em></span>
                          <input placeholder="Örn. 39,90 TL" />
                        </label>
                      </div>
                    </fieldset>

                    <fieldset className="setup-guide__fieldset">
                      <legend>Teslimat mesajı</legend>
                      <label className="setup-guide__field">
                        <span>Müşteriye gösterilecek bilgi metni</span>
                        <textarea rows={3} placeholder="Siparişiniz anlaşmalı kargo firmamızla takip numarası oluşturularak gönderilecektir." />
                      </label>
                    </fieldset>
                  </div>
                </section>
              )}

              {activeSetupDetailPanel && (
                <section className="setup-guide__section setup-guide__detail-section">
                  <div className="setup-guide__detail-intro">
                    <span className="setup-guide__info-icon">
                      <i className="fa-regular fa-sparkles" />
                    </span>
                    <div>
                      <h3>{activeSetupStep.title}</h3>
                      <p>{activeSetupDetailPanel.intro}</p>
                    </div>
                  </div>

                  {activeSetupDetailPanel.cards && (
                    <div className="setup-guide__detail-cards">
                      {activeSetupDetailPanel.cards.map((card) => (
                        <article key={card.title} className="setup-guide__detail-card">
                          <span>
                            <i className={`fa-regular ${card.icon}`} />
                          </span>
                          <strong>{card.title}</strong>
                          <p>{card.description}</p>
                        </article>
                      ))}
                    </div>
                  )}

                  <div className="setup-guide__detail-groups">
                    {activeSetupDetailPanel.groups.map((group) => (
                      <fieldset key={group.title} className="setup-guide__fieldset setup-guide__detail-group">
                        <legend>{group.title}</legend>
                        {group.description && <p className="setup-guide__detail-note">{group.description}</p>}

                        {group.fields && (
                          <div className="setup-guide__field-grid">
                            {group.fields.map((field) => (
                              <label key={field.label} className={`setup-guide__field${field.wide ? ' setup-guide__field--wide' : ''}`}>
                                <span>
                                  {field.label}
                                  {field.optional ? <small>Opsiyonel</small> : <em>*</em>}
                                </span>
                                <input type={field.type || 'text'} placeholder={field.placeholder || field.label} />
                              </label>
                            ))}
                          </div>
                        )}

                        {group.textarea && (
                          <label className="setup-guide__field">
                            <span>{group.textarea.label} <em>*</em></span>
                            <textarea rows={5} placeholder={group.textarea.placeholder} defaultValue={group.textarea.value} />
                          </label>
                        )}

                        {group.chips && (
                          <div className="setup-guide__detail-chips">
                            {group.chips.map((chip) => (
                              <label key={chip} className="setup-guide__detail-chip">
                                <input type="checkbox" />
                                <span>{chip}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </fieldset>
                    ))}
                  </div>

                  <div className="setup-guide__detail-actions">
                    <button type="button" className="setup-guide__ghost-action">
                      <i className="fa-regular fa-eye" />
                      Önizle
                    </button>
                    <button type="button" className="setup-guide__ghost-action">
                      <i className="fa-regular fa-circle-check" />
                      Kontrol et
                    </button>
                  </div>
                </section>
              )}

              {setupGuideStepIndex === 0 && (
                <>
                  <section className="setup-guide__video">
                    <div>
                      <i className="fa-regular fa-circle-play" />
                      <strong>{activeSetupStep.videoTitle || 'Kısa anlatım videosu'}</strong>
                      <span>Bu bölümle ilgili kısa anlatımı izleyerek birkaç dakika içinde ne yapmanız gerektiğini görebilirsiniz.</span>
                    </div>
                    <button type="button">Videoyu izle</button>
                  </section>

                  <section className="setup-guide__section">
                    <h3>Sık sorulan sorular</h3>
                    <div className="setup-guide__faq-list">
                      {activeSetupStep.faqs.map((faq) => (
                        <details key={faq.question} className="setup-guide__faq">
                          <summary>{faq.question}</summary>
                          <p>{faq.answer}</p>
                        </details>
                      ))}
                    </div>
                  </section>
                </>
              )}
            </main>

            <aside className="setup-guide__side">
              <div className="setup-guide__info-card">
                <span className="setup-guide__info-icon"><i className="fa-regular fa-clock" /></span>
                <strong>Tahmini süre</strong>
                <p>{activeSetupStep.estimate}</p>
              </div>
              <div className="setup-guide__info-card">
                <span className="setup-guide__info-icon"><i className="fa-regular fa-store" /></span>
                <strong>Seçili mağaza</strong>
                <p>{userStoreInfo?.firm_store_name || userStoreInfo?.firm?.name || 'Mağaza bilgisi'}</p>
              </div>
              <div className="setup-guide__help-card">
                <strong>Destek</strong>
                <p>Takıldığınız bir yer olursa kurulumdan çıkmadan destek ekibimize soru gönderebilirsiniz.</p>
              </div>
              {setupGuideStepIndex !== 0 && (
                <>
                  <section className="setup-guide__side-video">
                    <span className="setup-guide__info-icon"><i className="fa-regular fa-circle-play" /></span>
                    <strong>{activeSetupStep.videoTitle || 'Kısa anlatım videosu'}</strong>
                    <p>Bu bölümle ilgili kısa anlatımı izleyerek ne yapmanız gerektiğini görebilirsiniz.</p>
                    <button type="button">Videoyu izle</button>
                  </section>
                  <section className="setup-guide__side-faq">
                    <strong>Sık sorulan sorular</strong>
                    <div className="setup-guide__faq-list">
                      {activeSetupStep.faqs.map((faq) => (
                        <details key={faq.question} className="setup-guide__faq">
                          <summary>{faq.question}</summary>
                          <p>{faq.answer}</p>
                        </details>
                      ))}
                    </div>
                  </section>
                </>
              )}
            </aside>
          </div>

          <div className="setup-guide__footer">
            <div className="setup-guide__footer-left" />
            <div>
              <span>Değişiklikleriniz kaydedilir; hazır olmadığınız bölümlere daha sonra dönebilirsiniz.</span>
            </div>
            <div className="setup-guide__footer-actions">
              {activeSetupStep.id === 'visual-assets' && (
                <button
                  type="button"
                  className="setup-guide__footer-link"
                  onClick={skipSetupVisualAssetsStep}
                  disabled={setupCompanySaving || setupVisualAssetsSaving || setupPaymentsSaving}
                >
                  Bu adımı daha sonra ayarla
                </button>
              )}
              <button
                type="button"
                className="setup-guide__footer-btn"
                disabled={setupGuideStepIndex === 0 || setupCompanySaving || setupVisualAssetsSaving || setupPaymentsSaving}
                onClick={() => goToSetupGuideStep(setupGuideStepIndex - 1)}
              >
                <i className="fa-regular fa-arrow-left" />
                Önceki
              </button>
              <button
                type="button"
                className="setup-guide__footer-btn setup-guide__footer-btn--primary"
                onClick={() => goToSetupGuideStep(setupGuideStepIndex + 1)}
                disabled={setupGuideStepIndex === SETUP_GUIDE_STEPS.length - 1 || setupCompanySaving || setupVisualAssetsSaving || setupPaymentsSaving}
              >
                {setupCompanySaving || setupVisualAssetsSaving || setupPaymentsSaving ? 'Kaydediliyor' : 'Sonraki'}
                <i className={`fa-regular ${setupCompanySaving || setupVisualAssetsSaving || setupPaymentsSaving ? 'fa-spinner-third fa-spin' : 'fa-arrow-right'}`} />
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {isAdmin && (
      <Modal
        isOpen={setupTasksOpen}
        onClose={() => setSetupTasksOpen(false)}
        title="Görevler"
        description="Kurulum sonrası yapılacaklar"
        position="right"
        size="lg"
        noOverlay
        closeOnOutsideClick={false}
        closeOnEscape={false}
        contentClassName="setup-tasks-panel"
      >
        <div className="setup-tasks">
          <div className="setup-tasks__bar">
            <div>
              <strong>{setupTaskButtonLabel}</strong>
              <span>{Number(setupTaskSummary.required_open || 0)} yayın öncesi zorunlu · {Number(setupTaskSummary.training_open || 0)} eğitim bekliyor</span>
            </div>
            <button type="button" onClick={loadSetupTasks} disabled={setupTasksLoading}>
              <i className={setupTasksLoading ? 'fa-regular fa-spinner-third fa-spin' : 'fa-regular fa-arrows-rotate'} />
            </button>
          </div>

          <div className="setup-tasks__stats">
            <div>
              <span>Bekleyen</span>
              <strong>{openSetupTaskCount}</strong>
            </div>
            <div>
              <span>Tamamlanan</span>
              <strong>{Number(setupTaskSummary.done || 0)}</strong>
            </div>
            <div>
              <span>Toplam</span>
              <strong>{Number(setupTaskSummary.total || 0)}</strong>
            </div>
          </div>

          {setupTasksError && <div className="setup-tasks__error">{setupTasksError}</div>}

          <div className="setup-tasks__list">
            {setupTasksLoading && setupTasks.length === 0 && (
              <div className="setup-tasks__state">
                <i className="fa-regular fa-spinner-third fa-spin" />
                <span>Görevler yükleniyor.</span>
              </div>
            )}
            {!setupTasksLoading && setupTasks.length === 0 && !setupTasksError && (
              <div className="setup-tasks__state">
                <i className="fa-regular fa-circle-check" />
                <span>Bekleyen görev yok.</span>
              </div>
            )}
            {displayedSetupTasks.map((task) => {
              const isDone = task.status === 'done'
              return (
                <button
                  key={task.id}
                  type="button"
                  className={`setup-tasks__row${isDone ? ' is-done' : ''}`}
                  onClick={() => openSetupTaskTarget(task)}
                >
                  <input type="checkbox" checked={isDone} readOnly tabIndex={-1} />
                  <span>{task.title || 'Görev'}</span>
                </button>
              )
            })}
          </div>
        </div>
      </Modal>
      )}

      {isAdmin && (
        <Modal
          isOpen={storeSettingsOpen}
          onClose={handleStoreSettingsClose}
          title="Mağaza Ayarları"
          description="storeSettings helper cache çıktısı"
          position="right"
          size="lg"
          noOverlay={storeSettingsPinned}
          contentClassName={`store-settings-json-modal ${storeSettingsPinned ? 'store-settings-json-modal--pinned' : ''}`}
        >
          <div className="store-settings-json">
            <div className="store-settings-json__bar">
              <div>
                <strong>{storeSettings?.storeName || storeSettings?.storeCode || 'Mağaza Ayarları'}</strong>
                <span>
                  {storeSettings?.firmName || storeSettings?.firmCode || `Firma #${user?.firm_id || '-'}`} · {storeSettings?.storeDomain || `Mağaza #${user?.firm_store_id || '-'}`}
                </span>
              </div>
              <div className="store-settings-json__actions">
                <button
                  type="button"
                  className={storeSettingsPinned ? 'is-active' : ''}
                  onClick={handleStoreSettingsPin}
                  title={storeSettingsPinned ? 'Sabitlemeyi kapat' : 'Sağa sabitle'}
                >
                  <i className="fa-solid fa-thumbtack" />
                </button>
                <button type="button" onClick={handleStoreSettingsReset} disabled={storeSettingsResetting}>
                  <i className={storeSettingsResetting ? 'fa-regular fa-spinner-third fa-spin' : 'fa-regular fa-broom-wide'} />
                  {storeSettingsPinned ? '' : 'Cache Reset'}
                </button>
                <button type="button" onClick={loadStoreSettings} disabled={storeSettingsLoading}>
                  <i className={storeSettingsLoading ? 'fa-regular fa-spinner-third fa-spin' : 'fa-regular fa-arrows-rotate'} />
                  {storeSettingsPinned ? '' : 'Yenile'}
                </button>
              </div>
            </div>
            <div className="store-settings-json__tabs">
              {[
                { key: 'json', label: 'JSON' },
                { key: 'modules', label: 'Modüller' },
                { key: 'settings', label: 'Ayarlar' },
              ].map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  className={storeSettingsTab === tab.key ? 'is-active' : ''}
                  onClick={() => setStoreSettingsTab(tab.key as StoreSettingsTab)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            {storeSettingsError && (
              <div className="store-settings-json__error">{storeSettingsError}</div>
            )}
            {storeSettingsLoading && !storeSettings && (
              <div className="store-settings-json__state">Ayar JSONu yükleniyor...</div>
            )}
            {!storeSettingsLoading && !storeSettings && !storeSettingsError && (
              <div className="store-settings-json__state">Ayar kaydı bulunamadı.</div>
            )}
            {storeSettings && storeSettingsTab === 'json' && (
              <pre className="store-settings-json__pre">
                {JSON.stringify(storeSettings.cache, null, 2)}
              </pre>
            )}
            {storeSettings && storeSettingsTab === 'modules' && (
              <div className="store-settings-json__list">
                {(storeSettings.modules || []).map((module) => (
                  <div className="store-settings-json__module" key={module.id}>
                    <div>
                      <strong>{module.name}</strong>
                      <span>#{module.id}{module.shortDesc ? ` · ${module.shortDesc}` : ''}</span>
                    </div>
                    <em>{module.settingCount} ayar</em>
                  </div>
                ))}
              </div>
            )}
            {storeSettings && storeSettingsTab === 'settings' && (
              <div className="store-settings-json__settings">
                <div className="store-settings-json__search">
                  <i className="fa-regular fa-magnifying-glass" />
                  <input
                    value={storeSettingsSearch}
                    onChange={(event) => setStoreSettingsSearch(event.target.value)}
                    placeholder="Content, value veya modül ara"
                  />
                </div>
                <div className="store-settings-json__table">
                  <div className="store-settings-json__thead">
                    <span>Modül</span>
                    <span>Content</span>
                    <span>Value</span>
                  </div>
                  {settingsRows.map((row) => (
                    <div className="store-settings-json__row" key={String(row.id)}>
                      <span>{String(row.moduleId || '-')}</span>
                      <strong>{String(row.content || '-')}</strong>
                      <em>{String(row.value || '')}</em>
                    </div>
                  ))}
                  {settingsRows.length === 0 && (
                    <div className="store-settings-json__empty">Eşleşen ayar bulunamadı.</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </Modal>
      )}
    </header>
  )
}
